import React, { useRef, useState, useEffect, useCallback } from "react";
// 合入导航更新后，设置入口仍使用 SunMoon；必须显式导入，避免打开任何业务页时白屏。
import { SunMoon } from "lucide-react";
import { createPortal } from "react-dom";
import { NavLink, useNavigate, useLocation, Outlet, Navigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { LayoutDashboard, CalendarClock, KanbanSquare, Map as MapIcon, MapPinned, Crown, Bot, Upload, Bell, LogOut, Undo2, ShieldCheck, Briefcase, Percent, Archive, Camera, ClipboardCheck, FileEdit, ScrollText, Plug, Wallet, AtSign, Banknote, Trash2, Users, Waves, Workflow, ChevronDown, Building2, Boxes, Warehouse, BarChart3, AlertTriangle, PhoneCall, Headphones, PhoneOutgoing, BookOpen, HardHat, TrendingDown, Menu, X, Landmark, IdCard, Network, Clock, TrendingUp, UserPlus, Gift, Target, GraduationCap, Scale, LifeBuoy, UsersRound, CircleUserRound, PartyPopper, Star, Link2, Activity, FileStack, Calculator, ScanLine, CalendarDays, CreditCard, PanelLeftClose, PanelLeftOpen, Globe, HelpCircle, Search, GripVertical, Home, ListChecks, UserCheck, Handshake, Filter, MessageSquarePlus, MessageSquareText, MessagesSquare, Gauge, ArrowLeftRight, Megaphone, CheckCheck, ClipboardList, FileSignature, UserCog, Moon, Sun, BadgeCheck } from "lucide-react";
import WelcomeTour from "./WelcomeTour";
import GlobalSearch from "./GlobalSearch";
import { DisplayMenu } from "./DisplayMenu";
import { MobileMoreMenu } from "./MobileMoreMenu";
import MobileLanguageMenu from "./MobileLanguageMenu";
import { MobileNavigation, ResponsiveSidebar } from "./MobileNavigation";
import { InstallPrompt } from "./InstallPrompt";
import { useTheme, applySettings, takeAutoThemeHint } from "../lib/theme";
import { pushRecent } from "../lib/recent";
import { getNotifySettings, setNotifySettings, showDesktopNotification, popDesktopFor } from "../lib/notify";
import { setAllowedNavPaths, defaultLanding } from "../lib/navAccess";
import { chime } from "../lib/beep";
import { BrandLogo, BrandMark } from "./BrandLogo";
import Avatar from "./Avatar";
import ForcePasswordChange from "./ForcePasswordChange";
import Softphone from "./Softphone";
import { AssistantWidget } from "./AssistantWidget";
import WeekAgendaCalendar from "./WeekAgendaCalendar";
import { ClockWidget } from "./ClockWidget";
import { WorldClock } from "./WorldClock";
import { TodoPeek } from "./TodoPeek";
import { uploadChunked } from "../lib/upload";
import AvatarCropDialog from "./AvatarCropDialog";
import { toast } from "./ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { ImpersonationBanner } from "./ImpersonationBanner";
import { RecycleSurgeBanner } from "./RecycleSurgeBanner";
import { fmtDT, fmtText } from "../lib/date";
import { T } from "../lib/i18n";
import { guardBoardReturn } from "../lib/boardFilters";
import { HandoverInline } from "./HandoverInline";
import { reminderLink } from "./SubcontractOps";
import { NudgeReplyInline } from "./NudgeReplyInline";
import { PoolAssignedInline } from "./PoolAssignedInline";
import { DutyInline } from "./DutyInline";
import { RecycleRestoreInline } from "./RecycleRestoreInline";
import { StaleContactInline } from "./StaleContactInline";
import { openAuthedPdf } from "../lib/pdf";
import PageBreadcrumb from "./PageBreadcrumb";
// 员工使用分析采集：登录后在 Layout 里挂一次（登录页不采集），退出登录时立即结束会话。
import { useUsageTracking, stopUsageTracking } from "../lib/usageTracker";
import { useStaffLocationSharing } from "../hooks/useStaffLocationSharing";
import { BreadcrumbSlotContext } from "../context/BreadcrumbSlot";
import { UndoHistoryPanel } from "./UndoHistoryPanel";
import { FeedbackButton } from "./FeedbackButton";

const RailCtx = React.createContext(false);
const FavCtx = React.createContext({ favs: [], toggleFav: null });

// 铃铛分类页签的选中配色（反馈 FB973a773a3a）：评论=天蓝、催办=琥珀、系统=深灰，一眼能分清档位。
const BELL_CAT_ACTIVE = { comment: "bg-sky-600 text-white", nudge: "bg-amber-500 text-white", system: "bg-slate-900 text-white" };

function NavItem({ to, icon: Icon, label, dot, dotTestid, dotTo, dotTitle, exact, hint, testidPrefix = "nav-link" }) {
  const { moduleDenied } = useApp();
  const rail = React.useContext(RailCtx);
  const { favs, toggleFav } = React.useContext(FavCtx);
  const navTo = useNavigate();
  if (moduleDenied && moduleDenied(to)) return null;
  const pinned = favs.includes(to);
  const onDot = dotTo ? (e) => { e.preventDefault(); e.stopPropagation(); navTo(dotTo); } : undefined;
  return (
    <NavLink to={to} end={to === "/" || !!exact} title={rail ? label : undefined} data-path={to} data-testid={`${testidPrefix}-${to.replace(/\//g, "-").replace(/^-/, "") || "root"}`}
      className={({ isActive }) =>
        `group relative flex items-center gap-3 ${rail ? "justify-center px-0 py-2.5" : "px-3 py-2.5"} rounded-xl text-sm font-medium transition-all ${
          isActive ? "bg-brand-grad text-white shadow-brand" : "text-slate-600 hover:bg-brand-50/70 hover:text-brand-600"
        }`}>
      {({ isActive }) => (<>
        <Icon className="h-[18px] w-[18px] shrink-0" />
        {!rail && <span className="flex-1 truncate">{label}</span>}
        {!rail && hint && <kbd className={`text-[10px] font-mono px-1 rounded border ${isActive ? "border-white/40 text-white/80" : "border-slate-200 text-slate-400"}`} data-testid={`nav-fav-hint-${to.replace(/\//g, "-").replace(/^-/, "")}`}>{hint}</kbd>}
        {dot > 0 && <span role={onDot ? "button" : undefined} onClick={onDot} title={dotTitle} className={`${rail ? "absolute -top-0.5 -right-0.5" : ""} min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center ${onDot ? "cursor-pointer hover:bg-rose-600 hover:scale-110 transition-transform" : ""}`} data-testid={dotTestid || "rectify-nav-dot"} data-count={dot}>{dot > 999 ? "999+" : dot}</span>}
        {!rail && toggleFav && (
          <button type="button" data-testid={`nav-fav-toggle-${to.replace(/\//g, "-").replace(/^-/, "") || "root"}`} data-pinned={pinned ? "1" : "0"}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFav(to); }}
            title={pinned ? T("取消常用", "Unpin") : T("加入常用", "Pin to favorites")}
            className={`h-6 w-6 -mr-1 rounded-md flex items-center justify-center transition-opacity ${pinned ? "opacity-100" : "opacity-0 group-hover:opacity-100"} ${isActive ? "text-white/90 hover:bg-white/20" : "text-slate-400 hover:text-amber-500 hover:bg-amber-50"}`}>
            <Star className={`h-3.5 w-3.5 ${pinned ? (isActive ? "fill-white" : "fill-amber-400 text-amber-500") : ""}`} />
          </button>
        )}
      </>)}
    </NavLink>
  );
}

function NavSub({ id, label, icon: Icon, active, forceOpen, children }) {
  const rail = React.useContext(RailCtx);
  const storeKey = `navsub:${id}`;
  const [open, setOpen] = useState(() => {
    try { const v = localStorage.getItem(storeKey); return v === null ? active : v === "1"; } catch { return active; }
  });
  useEffect(() => { if (active || forceOpen) setOpen(true); }, [active, forceOpen]);
  const toggle = () => setOpen((o) => { const nv = !o; try { localStorage.setItem(storeKey, nv ? "1" : "0"); } catch {} return nv; });
  if (rail) return <div className="space-y-1" data-testid={`nav-sub-items-${id}`}>{children}</div>;
  return (
    <div>
      <button type="button" onClick={toggle} aria-expanded={open} data-testid={`nav-sub-${id}`} data-open={open ? "1" : "0"}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${active && !open ? "bg-brand-50 text-brand-600" : "text-slate-600 hover:bg-brand-50/70 hover:text-brand-600"}`}>
        <Icon className="h-[18px] w-[18px]" />
        <span className="flex-1 text-left">{label}</span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && <div className="ml-4 pl-2 border-l-2 border-slate-100 space-y-1 mt-1" data-testid={`nav-sub-items-${id}`}>{children}</div>}
    </div>
  );
}

function RailNavPopover({ label, icon: Icon, active, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-2 pb-2 border-b border-slate-100 last:border-0" data-testid={`nav-group-${label}`}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" aria-label={label} title={label} aria-expanded={open}
            data-testid={`nav-group-rail-trigger-${label}`}
            className={`w-full flex items-center justify-center rounded-xl py-2.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${active ? "bg-brand-grad text-white shadow-brand" : "text-slate-600 hover:bg-brand-50/70 hover:text-brand-600"}`}>
            <Icon className="h-[18px] w-[18px]" />
          </button>
        </PopoverTrigger>
        <PopoverContent side="right" align="start" sideOffset={8}
          className="z-[60] w-64 max-h-[70vh] overflow-y-auto rounded-xl p-2"
          data-testid={`nav-group-popover-${label}`}
          onClick={(event) => { if (!event.defaultPrevented && event.target.closest("a")) setOpen(false); }}>
          <div className="px-3 pb-2 pt-1 text-xs font-bold uppercase tracking-wider text-slate-500">{label}</div>
          <RailCtx.Provider value={false}><div className="space-y-1">{children}</div></RailCtx.Provider>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function NavGroup({ label, forceOpen, defaultOpen = true, railIcon, railActive = false, children }) {
  const rail = React.useContext(RailCtx);
  const storeKey = `navgroup:${label}`;
  const [open, setOpen] = useState(() => {
    try { const v = localStorage.getItem(storeKey); return v === null ? defaultOpen : v === "1"; } catch { return defaultOpen; }
  });
  useEffect(() => { if (forceOpen) setOpen(true); }, [forceOpen]);
  const toggle = () => setOpen((o) => { const nv = !o; try { localStorage.setItem(storeKey, nv ? "1" : "0"); } catch {} return nv; });
  if (rail && railIcon) return <RailNavPopover label={label} icon={railIcon} active={railActive}>{children}</RailNavPopover>;
  if (rail) return <div className="mb-2 pb-2 border-b border-slate-100 last:border-0 space-y-1" data-testid={`nav-group-${label}`}>{children}</div>;
  return (
    <div className="mb-1">
      <button onClick={toggle} aria-expanded={open} data-testid={`nav-group-${label}`}
        className="w-full flex items-center justify-between px-3 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wider text-slate-400 hover:text-slate-600 transition-colors">
        <span>{label}</span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && <div className="space-y-1">{children}</div>}
    </div>
  );
}

export default function Layout() {
  const { t, user, logout, lang, toggleLang, setLang, reminders, undo, canUndo, history, undoTo, updateAvatar, api, pathDenied, moduleDenied, moduleGranted, permOf, permMap, markReminderRead, markAllRemindersRead, myCardCount, visibleProjects } = useApp();
  const [procurementPendingCount, setProcurementPendingCount] = useState(0);
  // 使用分析采集器：只在这里启动一次。它自己处理可见性、5 分钟空闲、多标签选主与 60 秒心跳；
  // 分包商等外部账号也照常采集（他们不进员工统计，但会话数据能帮助判断外部账号是否异常）。
  useUsageTracking(api, user);
  const shareLocationAllowed = permOf("/hrms/me::share-location") === true;
  // 位置共享继续由全局 Layout 管理；侧栏不再显示提示，暂停入口保留在项目地图。
  useStaffLocationSharing(api, user, shareLocationAllowed);
  const nav = useNavigate();
  const location = useLocation();
  // CEO 侧栏显示待审批数量；其他角色不查询全公司待审批数据。
  useEffect(() => {
    if (user?.role !== "ceo") { setProcurementPendingCount(0); return undefined; }
    let alive = true;
    const refreshCount = () => api.get("/temp-material-procurements/pending-count")
      .then(({ data }) => { if (alive) setProcurementPendingCount(Number(data?.count) || 0); })
      .catch(() => { if (alive) setProcurementPendingCount(0); });
    refreshCount();
    const timer = window.setInterval(refreshCount, 60_000);
    const onProcurementChange = () => refreshCount();
    window.addEventListener("temp-material-procurement-updated", onProcurementChange);
    return () => { alive = false; window.clearInterval(timer); window.removeEventListener("temp-material-procurement-updated", onProcurementChange); };
  }, [api, user?.role, location.pathname]);
  const flatNavRef = useRef([]);
  // 面包屑条右侧的「动作插槽」：页面可 portal 按钮进来（见 context/BreadcrumbSlot.js）
  const breadcrumbSlotRef = useRef(null);
  /* 【2026-10-23 修复】这里原有一份与 lib/navAccess.js 重复的 LANDING 映射表，
     其中 eng_manager 仍指向已删除的 /rectify，两份表因此漂移。
     现在统一复用 defaultLanding()（登录落地页的唯一权威来源），
     避免同一个「角色 → 首页」映射再出现第三份副本。 */
  const onLanding = location.pathname === (defaultLanding(user) || "/");
  const showTopAgenda = onLanding && location.pathname !== "/crm/leads" && user.role !== "subcontractor";
  // 工程看板「返回令牌」全局守卫：若已离开看板去详情、却没返回而是拐去了别的页，作废令牌，
  // 避免下次回到 /board 时被误判为「从详情返回」而保留了本该清空的筛选条件。
  useEffect(() => { guardBoardReturn(location.pathname); }, [location.pathname]);
  /* 【2026-10-26 修复】Google 日历 OAuth 回调结果的统一提示（HRMS 走查一-2）。
     问题：后端 /api/public/gcal/callback 授权结束后会把整页重定向到 `/?gcal=<结果>`，
     但前端此前**没有任何代码消费这个参数** —— 员工在 Google 页面点了「允许」，被扔回首页，
     界面上既没有成功提示也没有失败提示，看起来像什么都没发生，容易被当成功能坏了。
     为什么放在 Layout：授权是整页跳转，回调固定落在根路径 `/`，而 `/` 对每个角色渲染的
     内容不同（销售看 CRM、工程看项目看板…），只有在全局布局里处理才能保证任何角色回来都收到反馈。
     处理完立刻把参数从地址栏抹掉（replace 不产生历史记录），避免刷新页面重复弹提示。

     为什么用 setTimeout 延后一拍（实测踩到的坑）：
     授权回来是**整页加载**，Layout 的 effect 在首帧就执行，而此时 App 末尾挂载的 <Toaster>
     还没完成订阅 —— 这时候调 toast.success() 会**静默丢弃**，用户依然什么都看不到。
     实测证据：同一段代码在页面已挂载后（SPA 内跳转、点保存按钮）能正常弹出，
     但 `goto('/?gcal=connected')` 整页加载后 [data-sonner-toast] 数量始终为 0。
     延后 300ms 让 Toaster 先挂载；清理 URL 仍放在同步阶段，保证地址栏立刻干净。

     两个必须注意的实现细节（都实测踩过）：
     1. 定时器**不能**放进 effect 的 cleanup。清理 URL 会让 location.search 变化，
        effect 随即重跑并执行上一轮的 cleanup，把刚设好的 toast 定时器当场取消 —— 
        第一版就是栽在这里，页面表现和没修一样。
     2. 用 ref 记录已提示过的结果值：React StrictMode 下 effect 会双跑，
        没有这道闸门就会连弹两次同样的提示。 */
  const gcalNoticeRef = useRef("");
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const res = q.get("gcal");
    if (!res) return;
    q.delete("gcal");
    const rest = q.toString();
    nav({ pathname: location.pathname, search: rest ? `?${rest}` : "", hash: location.hash }, { replace: true });
    if (gcalNoticeRef.current === res) return;
    gcalNoticeRef.current = res;
    setTimeout(() => {
      if (res === "connected") toast.success(lang === "zh" ? "Google 日历已连接；安排面试时会尝试邀请候选人，内部面试官使用系统提醒" : "Google Calendar connected. Interview invitations will be attempted for candidates; interviewers receive in-app notifications.");
      else if (res === "denied") toast.warning(lang === "zh" ? "你取消了 Google 日历授权，未连接" : "Authorization cancelled — not connected");
      else if (res === "failed") toast.error(lang === "zh" ? "Google 日历授权失败，请重试；若反复失败请让管理员检查客户端凭据与回调地址" : "Google Calendar authorization failed. Ask an admin to check the client credentials and redirect URI.");
      else if (res === "invalid") toast.error(lang === "zh" ? "授权链接已失效（可能超时或已被使用），请重新点「连接 Google 日历」" : "This authorization link is invalid or expired. Please start the connection again.");
    }, 300);
  }, [location.search, location.pathname, location.hash, nav, lang]);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [cropFile, setCropFile] = useState(null);
  const [bellOpen, setBellOpen] = useState(false);
  // 手机通知浮层同样支持键盘/返回键映射的 Escape，避免仅能精确点中背景才能收起。
  useEffect(() => {
    if (!bellOpen) return undefined;
    const close = (event) => { if (event.key === "Escape" && !event.defaultPrevented) { setBellOpen(false); document.querySelector('[data-testid="topbar-bell"]')?.focus(); } };
    document.addEventListener("keydown", close);
    return () => document.removeEventListener("keydown", close);
  }, [bellOpen]);
  // 铃铛下拉的分类页签（反馈 FB973a773a3a）：Comment（人写的评论 / @提及）、催办（概念方案催办
  // 一次几十条）与系统自动提醒三者分开，否则只显示前 8 条的混排里，反映项目真实进度的评论很容易被
  // 到期 / 逾期 / 催办类提醒挤掉。
  const [bellCat, setBellCat] = useState("all");   // all | comment | nudge | system
  const [bellDetail, setBellDetail] = useState(null);   // 下拉点条目 → 弹详情窗（不再直接跳转）
  const [celebration, setCelebration] = useState(null);
  const celebratedRef = useRef(new Set());
  useEffect(() => {
    const r = (reminders || []).find((x) => !x.read && x.type === "crm_sign_celebration" && x.toUser === user?.username && !celebratedRef.current.has(x.id));
    if (r) { celebratedRef.current.add(r.id); setCelebration(r); }
  }, [reminders, user]);
  const [myUnread, setMyUnread] = useState(0);
  const [collabInbox, setCollabInbox] = useState(0);
  const [chatUnread, setChatUnread] = useState(0);
  useEffect(() => {
    if (!user || user.role === "subcontractor") return undefined;
    const load = () => api.get("/chat/unread-summary").then(({ data }) => setChatUnread((data?.total || 0) + (data?.musePending || 0))).catch(() => setChatUnread(0));
    load();
    const t = setInterval(load, 60000);
    window.addEventListener("chat-unread-changed", load);
    return () => { clearInterval(t); window.removeEventListener("chat-unread-changed", load); };
  }, [api, user]);
  // 【2026-10-14】需求反馈（管理端）未读角标：60s 轮询 + 管理页内读/改状态后由 feedback-changed 事件即时刷新
  useEffect(() => {
    if (!user || !["admin", "ceo"].includes(user.role)) return undefined;
    const load = () => api.get("/admin/feedback/unread").then(({ data }) => setFbAdminUnread(data?.count || 0)).catch(() => {});
    const t = setInterval(load, 60000);
    window.addEventListener("feedback-changed", load);
    return () => { clearInterval(t); window.removeEventListener("feedback-changed", load); };
  }, [api, user]);
  const [unsentContracts, setUnsentContracts] = useState(0);
  const [poolWaiting, setPoolWaiting] = useState(0);
  const [unsignedOver7, setUnsignedOver7] = useState(0);
  const [signedDue, setSignedDue] = useState(0);
  const [teamOpenTodos, setTeamOpenTodos] = useState(0);
  // 【2026-10-28】只保留 CRM「客户今日必做」的明细（/me/agenda 里 kind=crm）。
  // 不再单独存 pending：顶栏角标只数待办（见 TodoPeek 的 dot 计算），侧栏 CRM 红点只数客户，
  // 各自从自己的数据源取数；混存一个「今天该处理的事」总数只会让两处口径再次串味。
  const [agendaCrm, setAgendaCrm] = useState([]);
  const [mobileTodoCount, setMobileTodoCount] = useState(0);
  const [constructionUnread, setConstructionUnread] = useState(0);
  const [mmReviewCount, setMmReviewCount] = useState(0);
  // 【2026-10-14】需求反馈：管理端侧栏「需求反馈」菜单的未读角标（仅 admin/ceo 拉取）
  const [fbAdminUnread, setFbAdminUnread] = useState(0);
  const [evalInvites, setEvalInvites] = useState({ pending: 0, total: 0 });
  const [eod, setEod] = useState(null);
  const [mobileNav, setMobileNav] = useState(false);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const [mobileMenuQuery, setMobileMenuQuery] = useState("");
  useEffect(() => { if (!mobileNav) setMobileMenuQuery(""); }, [mobileNav]);
  const [navFocusPath, setNavFocusPath] = useState("");
  const [rail, setRail] = useState(() => { try { return localStorage.getItem("ali_sidebar_rail") === "1"; } catch { return false; } });
  const toggleRail = () => setRail((v) => { const nv = !v; try { localStorage.setItem("ali_sidebar_rail", nv ? "1" : "0"); } catch {} return nv; });
  const [theme] = useTheme();
  const [prefs, setPrefs] = useState(null);
  const [favs, setFavs] = useState(() => { try { return JSON.parse(localStorage.getItem(`ali_fav:${user?.username}`)) || []; } catch { return []; } });
  const [tourOpen, setTourOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  // 操作撤销面板：顶栏「撤销」不再是静默回退，而是点开面板查看历史并选择撤销某一步
  const [undoOpen, setUndoOpen] = useState(false);
  const dragIdx = useRef(null);
  const [dragOver, setDragOver] = useState(null);
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "");
  // 定时 / 跟随系统 自动切换主题时提示一次（手动切换不提示）
  useEffect(() => {
    const show = (h) => {
      if (!h) return;
      const dark = h.theme === "dark";
      const by = h.mode === "schedule" ? (lang === "zh" ? `定时规则（${h.schedule?.from}–${h.schedule?.to}）` : `schedule (${h.schedule?.from}–${h.schedule?.to})`) : (lang === "zh" ? "系统外观" : "system appearance");
      toast(lang === "zh" ? `已按${by}切换为${dark ? "深色" : "浅色"}模式` : `Switched to ${dark ? "dark" : "light"} mode by ${by}`, {
        id: "theme-auto-hint", duration: 7000, icon: dark ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />,
        description: lang === "zh" ? "可在右上角「外观」菜单调整模式或时间段" : "Adjust mode or hours in the Appearance menu (top right)",
      });
    };
    show(takeAutoThemeHint());
    const h = (e) => show(e.detail);
    window.addEventListener("ali-theme-auto", h);
    return () => window.removeEventListener("ali-theme-auto", h);
  }, [lang]);
  useEffect(() => {
    if (!user?.username) return;
    api.get("/users/me/prefs").then(({ data }) => {
      setPrefs(data || {});
      if (Array.isArray(data?.favNav)) { setFavs(data.favNav); try { localStorage.setItem(`ali_fav:${user.username}`, JSON.stringify(data.favNav)); } catch {} }
      if (data?.theme || data?.darkFrom || data?.density || data?.fontScale || data?.contrast) {
        applySettings({ mode: data.theme, schedule: data.darkFrom && data.darkTo ? { from: data.darkFrom, to: data.darkTo } : undefined, density: data.density, fontScale: data.fontScale, contrast: data.contrast });
      }
      if (data && ("sound" in data || "desktopNotify" in data)) setNotifySettings({ ...(("sound" in data) ? { sound: !!data.sound } : {}), ...(("desktopNotify" in data) ? { desktop: !!data.desktopNotify } : {}) });
      try { if (data?.landing) localStorage.setItem(`ali_landing:${user.username}`, data.landing); else localStorage.removeItem(`ali_landing:${user.username}`); } catch {}
    }).catch(() => setPrefs({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.username]);
  useEffect(() => {
    if (!prefs || prefs.tourDone || window.innerWidth < 1024) return;
    if (localStorage.getItem(`ali_tour_v2:${user?.username}`) === "1") return;
    if (["cn_architect", "us_policy", "cn_design_manager", "us_design_manager", "design_manager", "subcontractor"].includes(user?.role)) { closeTour(false); return; }
    const t = setTimeout(() => setTourOpen(true), 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefs]);
  const closeTour = (done) => {
    setTourOpen(false);
    try { localStorage.setItem(`ali_tour_v2:${user?.username}`, "1"); } catch {}
    if (!prefs?.tourDone) { setPrefs((p) => ({ ...(p || {}), tourDone: true })); api.put("/users/me/prefs", { tourDone: true }).catch(() => {}); }
    if (done) toast.success(lang === "zh" ? "欢迎使用新版界面！顶栏「?」可随时重看引导" : "Welcome to the new UI! Click ? in the top bar to replay the tour");
  };
  const savePrefs = (patch) => api.put("/users/me/prefs", patch).catch(() => {});
  const saveFavs = (nv, msg) => {
    setFavs(nv);
    try { localStorage.setItem(`ali_fav:${user?.username}`, JSON.stringify(nv)); } catch {}
    return api.put("/users/me/prefs", { favNav: nv }).then(() => msg && toast.success(msg)).catch(() => toast.error(lang === "zh" ? "保存失败" : "Save failed"));
  };
  const moveFav = (from, to) => {
    if (from === to || from == null || to == null) return;
    const nv = [...favs]; const [it] = nv.splice(from, 1); nv.splice(to, 0, it);
    saveFavs(nv);
  };
  useEffect(() => {
    const h = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      const editing = ["input", "textarea", "select"].includes(tag) || e.target?.isContentEditable;
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === "k") { e.preventDefault(); setSearchOpen((o) => !o); return; }
      if (e.altKey && !e.ctrlKey && !e.metaKey && !editing && /^Digit[1-6]$/.test(e.code)) {
        const i = parseInt(e.code.slice(5), 10) - 1;
        if (favs[i]) { e.preventDefault(); nav(favs[i]); }
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [favs, nav]);
  useEffect(() => {
    const focusNav = (e) => {
      const path = String(e.detail?.path || "").split("?")[0];
      if (!path) return;
      setRail(false);
      if (window.innerWidth < 1024) setMobileNav(true);
      setNavFocusPath(path);
      window.setTimeout(() => setNavFocusPath(""), 1200);
    };
    window.addEventListener("ali:sidebar-focus", focusNav);
    return () => window.removeEventListener("ali:sidebar-focus", focusNav);
  }, []);
  useEffect(() => {
    if (!navFocusPath) return;
    const timer = window.setTimeout(() => {
      const link = Array.from(document.querySelectorAll('[data-testid="sidebar-nav"] [data-path]'))
        .find((el) => el.getAttribute("data-path") === navFocusPath);
      link?.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 100);
    return () => window.clearTimeout(timer);
  }, [navFocusPath, rail, mobileNav]);
  useEffect(() => {
    // 【2026-10-28 代操作不写浏览痕迹】代操作期间 Layout 以为自己是目标账号，若照常记录，
    // 操作者浏览过的页面会被写进**目标账号**的「最近访问 / 登录首页」，目标账号本人下次
    // 登录就看到一串自己没去过的页面。老板要求代操作就落到账号自己的默认页、不要带着
    // 上一个操作者的浏览记录，所以这里整段跳过（退出代操作后照常记录）。
    if (user?.impersonatedBy) return;
    const mp = location.pathname.match(/^\/project\/([^/]+)/);
    if (mp) {
      const pj = (visibleProjects || []).find((x) => x.id === mp[1]);
      if (pj) pushRecent(user?.username, { type: "project", id: pj.id, title: pj.address, sub: pj.id });
      return;
    }
    const ml = location.pathname.match(/^\/crm\/leads\/([^/]+)/);
    if (ml) { api.get(`/crm/leads/${ml[1]}`).then(({ data }) => data && pushRecent(user?.username, { type: "lead", id: data.id || ml[1], title: data.clientName || `${data.firstName || ""} ${data.lastName || ""}`.trim() || ml[1], sub: data.phone || data.city || "" })).catch(() => {}); return; }
    const mc = location.pathname.match(/^\/chat\/c\/([^/]+)/);
    if (mc) return;   // 频道由 ⌘K 选择时记录（含名称）
    const pg = flatNavRef.current.find((it) => it.to === location.pathname);   // 最近访问的页面（侧栏菜单项）
    // 首页不记入「最近访问」（它本来就是默认落地页）。这里与上面的 onLanding 同源，
    // 统一走 defaultLanding()，不再引用已被删除的本地 LANDING 映射表。
    if (pg && pg.to !== (defaultLanding(user) || "/")) pushRecent(user?.username, { type: "page", id: pg.to, title: pg.label, sub: pg.to });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, visibleProjects, user?.impersonatedBy]);
  const setLanding = (path) => {
    try { if (path) localStorage.setItem(`ali_landing:${user?.username}`, path); else localStorage.removeItem(`ali_landing:${user?.username}`); } catch {}
    setPrefs((p) => ({ ...(p || {}), landing: path || null }));
    api.put("/users/me/prefs", { landing: path || null }).then(() => toast.success(path ? (lang === "zh" ? "已设置登录首页" : "Landing page saved") : (lang === "zh" ? "已恢复角色默认首页" : "Back to role default"))).catch(() => toast.error(lang === "zh" ? "保存失败" : "Save failed"));
  };
  const toggleFav = (to) => {
    const has = favs.includes(to);
    if (!has && favs.length >= 6) { toast.warning(lang === "zh" ? "常用最多 6 个，请先取消一个" : "Up to 6 favorites — unpin one first"); return; }
    const nv = has ? favs.filter((x) => x !== to) : [...favs, to];
    saveFavs(nv, has ? (lang === "zh" ? "已从常用移除" : "Removed from favorites") : (lang === "zh" ? "已加入常用" : "Pinned to favorites"));
  };
  const handleLogout = async () => {
    try {
      const { data } = await api.get("/me/agenda");
      if (data && data.gateEnabled && !data.isManager && !data.clockedOut && data.pending > 0) { setEod(data); return; }
    } catch { /* ignore */ }
    // 先结束使用会话：把剩余增量与「已结束」立即写回，避免会话时长一直挂到 15 分钟超时；
    // stopUsageTracking 内部用的是 keepalive 请求，不 await，不会拖慢退出。
    stopUsageTracking("logout");
    logout(); nav("/login");
  };
  const eodClockOut = async () => {
    try { await api.post("/me/clock-out"); logout(); nav("/login"); }
    catch (e) { setEod((d) => ({ ...d, err: e?.detailText || "无法打卡下班" })); }
  };
  // 二-7：原 /urges?status=open 轮询（用于整改看板红点 escalatedCount）已随整改看板删除。
  useEffect(() => {
    if (user && ["admin", "ceo", "sales_vp", "sales_manager"].includes(user.role)) {
      api.get("/crm/contracts/unsent?days=3").then(({ data }) => setUnsentContracts(data?.count || 0)).catch(() => setUnsentContracts(0));
      api.get("/crm/pool/count").then(({ data }) => setPoolWaiting(data?.count || 0)).catch(() => setPoolWaiting(0));
    }
    if (user && ["admin", "ceo", "sales", "sales_vp", "sales_manager", "marketing", "eng_manager"].includes(user.role)) {
      api.get("/crm/contracts/unsigned?summaryOnly=1").then(({ data }) => setUnsignedOver7(data?.summary?.over7 || 0)).catch(() => setUnsignedOver7(0));
    }
    // 财务已签约客户入口与后端全公司读取范围一致，待收数量也使用同一接口。
    if (user && ["admin", "ceo", "sales", "sales_vp", "sales_manager", "marketing_manager", "finance"].includes(user.role)) {
      api.get("/crm/signed/due-count").then(({ data }) => setSignedDue(data?.count || 0)).catch(() => setSignedDue(0));
    }
    // 【2026-10-14】需求反馈未读角标（管理端）：与其它角标同批拉取，随后由下方定时器刷新
    if (user && ["admin", "ceo"].includes(user.role)) {
      api.get("/admin/feedback/unread").then(({ data }) => setFbAdminUnread(data?.count || 0)).catch(() => setFbAdminUnread(0));
    }
    if (user) {
      api.get("/crm/site-eval-invites/mine?summaryOnly=1").then(({ data }) => setEvalInvites({ pending: data?.pending || 0, total: data?.total || 0 })).catch(() => setEvalInvites({ pending: 0, total: 0 }));
    }
    if (user) {
      api.get("/my-mentions/unread-count").then(({ data }) => setMyUnread(data?.count || 0)).catch(() => setMyUnread(0));
      if (user.role !== "subcontractor") api.get("/collab/tasks", { params: { box: "inbox" } }).then(({ data }) => setCollabInbox(data?.counts?.inbox || 0)).catch(() => setCollabInbox(0));
      if (["admin", "ceo", "design_manager", "sales_vp", "sales_manager", "eng_manager", "pm", "finance", "warehouse_manager"].includes(user.role)) api.get("/todos/manager/dashboard").then(({ data }) => setTeamOpenTodos(data?.summary?.open || 0)).catch(() => setTeamOpenTodos(0));
      // 【2026-10-28】只取 kind=crm 的明细给顶栏弹层与侧栏 CRM 红点用。
      // 顶栏角标不再需要这里的 pending —— 它只数待办，口径与「我的待办」页一致。
      const applyAgenda = (data) => {
        setAgendaCrm(data && !data.isManager ? (data.todayItems || []).filter((it) => it.kind === "crm") : []);
      };
      api.get("/me/agenda").then(({ data }) => applyAgenda(data)).catch(() => applyAgenda(null));
    }
    const onEvalChanged = () => { if (user) api.get("/crm/site-eval-invites/mine?summaryOnly=1").then(({ data }) => setEvalInvites({ pending: data?.pending || 0, total: data?.total || 0 })).catch(() => {}); };
    // 【SYS-007】客户邮箱补齐 → 侧栏「客户线索 CRM」上的未发送角标要跟着减。
    // 列表页与合同工作区保存后会广播 crm-lead-changed；这里只对原本就有权看到该角标的角色重拉。
    const onLeadChanged = () => {
      if (user && ["admin", "ceo", "sales_vp", "sales_manager"].includes(user.role)) {
        api.get("/crm/contracts/unsent?days=3").then(({ data }) => setUnsentContracts(data?.count || 0)).catch(() => {});
      }
    };
    const onTodosChanged = () => {   // 每日待办任一处写入 → 侧栏 / 顶栏计数同步
      if (!user) return;
      if (["admin", "ceo", "design_manager", "sales_vp", "sales_manager", "eng_manager", "pm", "finance", "warehouse_manager"].includes(user.role)) api.get("/todos/manager/dashboard").then(({ data }) => setTeamOpenTodos(data?.summary?.open || 0)).catch(() => {});
      // 与首次加载同口径，只刷新 CRM 明细（顶栏角标由 TodoPeek 自己按待办数算）。
      api.get("/me/agenda").then(({ data }) => {
        setAgendaCrm(data && !data.isManager ? (data.todayItems || []).filter((it) => it.kind === "crm") : []);
      }).catch(() => {});
    };
    window.addEventListener("eval-invites-changed", onEvalChanged);
    window.addEventListener("todos-changed", onTodosChanged);
    window.addEventListener("crm-lead-changed", onLeadChanged);
    return () => { window.removeEventListener("eval-invites-changed", onEvalChanged); window.removeEventListener("todos-changed", onTodosChanged); window.removeEventListener("crm-lead-changed", onLeadChanged); };
  }, [api, user]);

  /* 「待复核」侧栏角标【2026-10-29 修正】
     原实现把拉取条件写死成 ["admin","ceo","marketing","marketing_manager"]：
     管理员在「角色管理」里把 /crm/mm-review 授予别的角色（线上 2026-10-29 已实际授予 sales_vp）后，
     侧栏入口因为读权限快照所以显示出来了，但角标压根不会发请求 —— 永远没有数字，
     看起来像「待复核池是空的」，而不是「这个账号没被算进角标名单」。这与项目红线
     「权限必须走配置、不许写死角色」相悖，也会让管理员以为授权只成功了一半。

     现在的判定顺序（与侧栏入口 vis() 完全同口径）：
       · 权限快照已加载 → 服务端最终判定 permOf("/crm/mm-review")（含账号级、自定义角色、
         角色矩阵与子键继承），true 才拉；
       · 快照还没回来 → 退回硬编码名单兜底，保证首屏不会因为等快照而少显示角标，
         行为与改造前一致。
     依赖里带上 permMap 是关键：快照是异步加载的，快照到达后本 effect 必须重跑一次，
     否则本次登录要刷新页面才看得到角标（授权当场不生效）。
     无权限时后端返回 403，下面的 .catch 会安静把角标置 0，不会弹错。 */
  useEffect(() => {
    if (!user) { setMmReviewCount(0); return undefined; }
    const snap = permOf("/crm/mm-review");
    const allowed = snap !== undefined
      ? snap
      : ["admin", "ceo", "marketing", "marketing_manager"].includes(user.role);
    if (!allowed) { setMmReviewCount(0); return undefined; }
    let alive = true;
    const load = () => api.get("/crm/mm-review/count")
      .then(({ data }) => { if (alive) setMmReviewCount(data?.count || 0); })
      .catch(() => { if (alive) setMmReviewCount(0); });
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(t); };
  }, [api, user, permMap, permOf]);

  const loadConstructionUnread = useCallback(() => {
    if (user && (user.role === "sales" || user.salesName)) {
      api.get("/my-construction-projects/unread-count").then(({ data }) => setConstructionUnread(data?.count || 0)).catch(() => setConstructionUnread(0));
    }
  }, [api, user]);
  useEffect(() => {
    loadConstructionUnread();
    window.addEventListener("construction-unread-changed", loadConstructionUnread);
    return () => window.removeEventListener("construction-unread-changed", loadConstructionUnread);
  }, [loadConstructionUnread]);
  // 「新项目待开工」角标：管理层从任何页面都能在「工程项目」菜单看到数量（横幅本身只在 /board 显示）
  const [kickoffCount, setKickoffCount] = useState(0);
  useEffect(() => {
    if (!user || !["admin", "ceo", "eng_manager"].includes(user.role)) { setKickoffCount(0); return undefined; }
    const load = () => api.get("/kickoff/pending").then(({ data }) => setKickoffCount((data?.items || []).length)).catch(() => {});
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    window.addEventListener("kickoff-changed", load);
    return () => { clearInterval(t); window.removeEventListener("kickoff-changed", load); };
  }, [api, user]);
  const seenReminderIds = useRef(null);
  useEffect(() => {
    if (!user) return;
    // 与新口径一致：后端已按受众过滤并按「我」计算已读，这里不再做前端二次筛选
    // （旧逻辑会漏掉 toUser 写成脏用户名 / team 为空的广播提醒 → 桌面通知也不弹）。
    const list = reminders.filter((r) => !r.read);
    const ids = new Set(list.map((r) => r.id));
    if (seenReminderIds.current === null) { seenReminderIds.current = ids; return; }
    const fresh = list.filter((r) => !seenReminderIds.current.has(r.id));
    seenReminderIds.current = ids;
    if (!fresh.length) return;
    /* 【2026-10-23 用户需求】「聊天中有电脑系统的提示，只弹出艾特自己的」：
       弹窗 / 铃声只认设计阶段会话里真正 @我 / 回复我 的提醒（category=comment），
       其余设计类系统提醒（复核、状态、审批、逾期…）照旧进铃铛，只是不再弹桌面。
       其它模块的提醒不过滤，行为与改动前完全一致（见 lib/notify.popDesktopFor）。 */
    const popList = fresh.filter(popDesktopFor);
    if (!popList.length) return;
    const ns = getNotifySettings();
    if (ns.sound) chime();
    if (ns.desktop && document.visibilityState !== "visible") {
      const first = popList[0];
      const title = popList.length > 1 ? (lang === "zh" ? `Aligcus · ${popList.length} 条新提醒` : `Aligcus · ${popList.length} new reminders`) : (lang === "zh" ? "Aligcus · 新提醒" : "Aligcus · New reminder");
      /* 桌面通知点击同样走 reminderCenterPath() 兜底：这个 effect 在渲染结束后才执行，
         reminderCenterPath 届时已初始化；无 link 的提醒不再把无权账号送去拒绝页。 */
      showDesktopNotification(title, (first && bellMsg(first)) || "", () => nav(reminderLink(first) || reminderCenterPath()));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reminders, user]);
  if (!user) { nav("/login"); return null; }

  // 个人菜单头像：选图 → 方形裁剪 → 分片上传对象存储 → 绑定账号
  const onPickAvatar = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) setCropFile(file);
  };
  const onCropped = async (blob) => {
    setUploading(true);
    try {
      const r = await uploadChunked(api, blob, { name: "avatar.jpg", type: "image/jpeg" });
      await updateAvatar({ fileId: r.fileId });
      toast.success(lang === "zh" ? "头像已更新" : "Avatar updated");
      setCropFile(null);
    } catch (err) { toast.error(err?.response?.data?.detail || err.message); }
    setUploading(false);
  };

  const isMember = user.role === "member";
  const isFinance = user.role === "finance";
  const isCeo = user.role === "ceo";
  const isAdmin = ["admin", "ceo"].includes(user.role);
  const isEngMgr = user.role === "eng_manager";
  const isCdc = user.role === "cdc";
  const isSales = user.role === "sales";
  const isAftersales = user.role === "aftersales";
  const isBranchSalesMgr = user.role === "branch_sales_manager";
  const isBranchView = isAftersales || isBranchSalesMgr;
  const isPm = user.role === "pm";
  const isCashier = user.role === "cashier";
  const isSub = user.role === "subcontractor";
  const isWhMgr = user.role === "warehouse_manager";
  const isWhBuyer = user.role === "material_purchaser";
  const isWhAudit = user.role === "warehouse_auditor";
  const isWhRole = isWhMgr || isWhBuyer || isWhAudit;
  const isCrm = ["sales", "sales_manager", "sales_vp", "marketing", "marketing_manager"].includes(user.role);
  const isCrmMgr = ["sales_manager", "sales_vp", "marketing", "marketing_manager"].includes(user.role) || isAdmin || isCeo;
  const isMMReviewer = ["marketing", "marketing_manager"].includes(user.role) || isAdmin || isCeo;
  const isLostAnalytics = ["marketing", "marketing_manager", "sales_vp", "sales_manager"].includes(user.role) || isAdmin || isCeo;
  const isTm = ["telesales", "tm_manager", "b2b_sales", "data_importer"].includes(user.role);
  const isTmView = ["tm_manager", "data_importer", "sales_manager", "sales_vp", "marketing", "marketing_manager"].includes(user.role) || isAdmin || isCeo;
  const isDesign = ["design_manager", "cn_architect", "us_policy"].includes(user.role);
  const isDesignMgr = ["admin", "ceo", "design_manager"].includes(user.role);
  const isHrFull = user.role === "hr" || isCeo || isAdmin;
  const isDeptMgr = ["eng_manager", "design_manager", "sales_manager", "sales_vp", "marketing", "marketing_manager", "warehouse_manager", "finance"].includes(user.role);
  // 使用分析入口默认给「各条线经理」：与后端 MODULE_CATALOG 的 /admin/usage 默认角色、
  // 以及 usage_analytics.MANAGER_SCOPE 的键**三处严格一致**（改一处必须同步另外两处）。
  // 之所以不含 hr / finance：它们在服务端没有部门数据范围，落到 self 后打开页面几乎全空，
  // 实测确认这个组合很误导。能进页面 ≠ 能看全公司——数据范围仍由服务端按岗位收窄。
  const isUsageViewer = ["eng_manager", "design_manager", "sales_vp", "sales_manager", "branch_sales_manager", "marketing_manager", "warehouse_manager"].includes(user.role);
  // 2026-10-20：未读列表改为**直接信任后端**的受众与已读判定。
  //   原有前端二次过滤 `r.toUser === user.username || inUserTeam(...)` 有两个问题：
  //     ① 后端已经把「不属于我」的提醒过滤掉了，前端再用严格字符串比较一遍，
  //        会把 toUser 写成脏用户名（`Jose` / `cassie `）的提醒误判成别人的 → 角标永远不亮；
  //     ② member 账号遇到 team 为空的广播型提醒（如项目收款）会被前端整条丢掉 → 「一条都收不到」。
  //   后端 `read` 现在按当前登录人计算（广播型历史文档走 readByUsers），所以这里只需看 read。
  const unreadList = reminders.filter((r) => !r.read);
  const unread = unreadList.length;
  // 分类计数 + 当前页签下的列表（category 由后端 /reminders 直接返回，缺省按 system 处理）
  const catOf = (r) => r.category || "system";
  const bellCatCounts = { all: unreadList.length, comment: unreadList.filter((r) => catOf(r) === "comment").length, nudge: unreadList.filter((r) => catOf(r) === "nudge").length, system: unreadList.filter((r) => catOf(r) === "system").length };
  const bellList = bellCat === "all" ? unreadList : unreadList.filter((r) => catOf(r) === bellCat);
  // 兜底：老数据里开票类提醒（invoice/invoiced/paid）没写 message，铃铛只渲染 message 会变成空条目——按类型补文案
  const BELL_TYPE_FALLBACK = {
    invoice: ["节点已完成 — 请向客户开具收款单", "Milestone completed — issue invoice to client"],
    invoiced: ["已向客户开具收款单", "Invoice sent to client"],
    paid: ["客户已付款", "Client payment received"],
  };
  const bellMsg = (r) => {
    if (r.message) return r.message;
    const fb = BELL_TYPE_FALLBACK[r.type];
    if (!fb) return "";
    const detail = [r.address, r.amount != null && r.amount !== "" ? `$${r.amount}` : ""].filter(Boolean).join(" · ");
    return `${lang === "zh" ? fb[0] : fb[1]}${detail ? `：${detail}` : ""}`;
  };
  const toggleBell = () => setBellOpen((o) => !o);
  // 铃铛下拉：点条目只弹详情窗（弹窗展示即已读）；是否跳转由弹窗里的「去处理」按钮决定
  const openBellDetail = (r) => {
    setBellDetail(r);
    setBellOpen(false);
    // 标记已读失败（如后端不可达）不应影响弹窗展示，也不该抛出未捕获异常
    if (!r.read) Promise.resolve(markReminderRead(r.id)).catch(() => {});
  };
  /* 【2026-10-28 修复】提醒中心落点统一收口：能进 /reminders 的账号（快照判定）才回落到提醒中心，
     否则回该账号的默认首页。此前铃铛对分包商也渲染，而分包商既没有 /reminders 菜单、目录判定也为 false，
     点「查看全部提醒」/ 无 link 的提醒时会被送到「无权访问该模块」的空白页。
     为什么放在这里而不是逐处加 isSub：三个入口（桌面通知点击 / 详情「去处理」/ 下拉底部按钮）都走这条兜底，
     收口一处即可保证「铃铛能点开的页面，落到的一定是它有权看的页面」。
     注意这里必须是**函数**而不是常量：vis() 在下方（NAVDEF 之前）才定义，常量会在渲染到这里时
     撞上暂时性死区；写成函数只在点击时求值，那时 vis 早已就绪。 */
  const reminderCenterPath = () => (vis("/reminders", !isSub) ? "/reminders" : (defaultLanding(user) || "/"));
  const bellGo = (r) => { setBellDetail(null); nav(reminderLink(r) || reminderCenterPath()); };
  const renderBellInline = (r) => (
    <>
      {["hrms_dismiss", "hrms_task_remind", "hrms_handover_ack", "hrms_handover_nudge"].includes(r.type) && r.transitionId && (
        <div className="px-4 pb-2"><HandoverInline reminder={r} compact /></div>
      )}
      {["hrms_duty", "hrms_duty_nudge"].includes(r.type) && r.dutyId && (
        <div className="px-4 pb-2"><DutyInline reminder={r} compact onChanged={() => markReminderRead(r.id)} /></div>
      )}
      {r.type === "progress_nudge" && r.nudgeId && (
        <div className="px-4 pb-2"><NudgeReplyInline reminder={r} compact onChanged={() => setTimeout(() => markReminderRead(r.id), 2500)} /></div>
      )}
      {r.type === "pool_rule_assigned" && (r.leadIds || []).length > 0 && (
        <div className="px-4 pb-2"><PoolAssignedInline reminder={r} compact onAllDone={() => setTimeout(() => markReminderRead(r.id), 1200)} /></div>
      )}
      {r.type === "recycle_expiring" && r.recycleId && (
        <div className="px-4 pb-2"><RecycleRestoreInline reminder={r} compact onChanged={() => setTimeout(() => markReminderRead(r.id), 2000)} /></div>
      )}
      {r.type === "aftersales_stale" && Array.isArray(r.top) && r.top.length > 0 && (
        <div className="px-4 pb-2"><StaleContactInline reminder={r} compact onAllDone={() => setTimeout(() => markReminderRead(r.id), 1500)} /></div>
      )}
    </>
  );
  const isApprover = ["admin", "ceo", "sales_vp", "sales_manager"].includes(user.role);
  const pendingApprovals = isApprover
    ? reminders.filter((r) => r.type === "price_approval" && r.toUser === user.username).length
    : 0;
  const newVisits = ["sales", "sales_manager", "sales_vp", "admin", "ceo"].includes(user.role)
    ? reminders.filter((r) => r.type === "site_visit_request" && !r.read && r.toUser === user.username).length
    : 0;

  // Per-item visibility【2026-10-24 改造】权限快照优先：服务端已把账号级授予/屏蔽、自定义角色、
  // 角色矩阵与目录默认（含子键继承）一次算好（permMap），菜单可见性与后端接口准入完全同源 ——
  // 管理员配置后无需改代码即可从菜单到接口全链路生效。快照未覆盖该键（目录外入口）或尚未加载
  // 时，退回原三级判定（账号授予 > 屏蔽 > def），行为与改造前一致。
  // when：纯运行时条件（与角色无关，如「销售助理数为 0」），快照放行后仍需满足才显示。
  const vis = (key, def, when) => {
    const snap = permOf(key);
    if (snap !== undefined) return snap && (when === undefined || !!when);
    return moduleGranted(key) ? true : moduleDenied(key) ? false : !!def;
  };
  const zh = lang === "zh";
  const NAVDEF = [
    { key: "overview", label: zh ? "概览" : "Overview", items: [
      { to: "/overview", icon: LayoutDashboard, label: t("dashboard"), def: isAdmin || isCeo || isFinance || isEngMgr },
      // 【2026-10-12 修改】原 def: isCeo 只让 ceo 看到 CEO 看板入口，与后端 /ceo/* 的准入
      // (CEO_VIEW_ROLES 含 admin / finance) 不一致 —— 结果是 admin 看不到菜单，只能靠单账号
      // modAllowed 手工授权才进得去。改为 isAdmin || isFinance（isAdmin 本身已含 ceo），
      // 与 server.py MODULE_CATALOG 里 /ceo 的默认角色 ["admin","ceo","finance"] 对齐。
      // 行政中心有独立菜单，CEO 首页用精确匹配避免两个入口同时高亮。
      { to: "/ceo", icon: Crown, label: t("ceoBoard"), def: isAdmin || isFinance, exact: true },
      // 行政申请面向内部员工；具体事项及签署由独立后端授权，外部账号不显示此入口。
      { to: "/ceo/administration", icon: FileSignature, label: zh ? "行政审批与签署" : "Administrative Approvals", def: !["subcontractor", "owner", "vendor"].includes(user.role) },
    ] },
    // 用户本轮要求将广告物料管理归入行政办公；保留原路径作为权限键和历史深链，菜单仍取服务端 effective 快照。
    { key: "office-admin", label: zh ? "行政办公" : "Office Administration", items: [
      { to: "/administration/office-supplies", icon: Boxes, label: zh ? "办公用品管理" : "Office Supplies" },
      { to: "/crm/ad-materials", icon: Boxes, label: zh ? "广告物料管理" : "Advertising Materials", def: false },
    ] },
    { key: "crm", label: zh ? "销售 / CRM" : "Sales / CRM", items: [
      { to: "/crm/dashboard", icon: LayoutDashboard, label: zh ? "CRM 看板" : "CRM Dashboard", def: isCrmMgr },
      // 【2026-10-28 口径修正】这里的红点以前用 agendaPending，而它是「今天该处理的事」=
      // 个人待办 + CRM 客户必做混在一起的数 —— 于是「客户线索 CRM」的红点会被待办数量撑起来
      // （明明没有任何客户要跟进，红点却挂着）。改为只用 CRM 自身的客户数（agendaCrm.length），
      // 与顶栏「每日待办」角标（只数待办）各归各位，两个入口不再互相污染。
      { to: "/crm/leads", icon: Users, label: zh ? "客户线索 CRM" : "Leads CRM", def: isCrm || isAdmin || isCeo, dot: unsentContracts || newVisits || agendaCrm.length, dotTestid: "crm-leads-nav-dot" },
      { to: "/crm/signed", icon: BadgeCheck, label: zh ? "已签约客户" : "Signed Customers", def: ["sales", "sales_manager", "sales_vp", "marketing_manager", "finance"].includes(user.role) || isAdmin || isCeo, dot: signedDue, dotTestid: "signed-due-nav-dot", dotTo: "/crm/signed?due=1", dotTitle: zh ? `${signedDue} 位已签约客户有已开票未收款 · 点击直达「立刻收款」` : `${signedDue} signed customers invoiced but unpaid · click to view` },
      { to: "/crm/payment-calendar", icon: CalendarDays, label: zh ? "收款日历" : "Payment Calendar", def: ["sales", "sales_manager", "sales_vp", "marketing_manager"].includes(user.role) || isAdmin || isCeo },
      { to: "/referral", icon: Handshake, label: zh ? "转介绍 / 合作伙伴" : "Referral Partners", def: isCrmMgr || isFinance || isAftersales },
      { to: "/aftersales/customers", icon: UsersRound, label: zh ? "老客户清单" : "Past Customers", def: isAftersales || ["sales_manager", "sales_vp"].includes(user.role) || isAdmin || isCeo },
      { sub: "pool", icon: Waves, label: zh ? "客户公海" : "Public Pool", items: [
        { to: "/crm/pool", icon: Waves, label: zh ? "公海客户" : "Pool Leads", def: (isCrm || isAdmin || isCeo) && user.role !== "marketing_manager", dot: poolWaiting, dotTestid: "pool-waiting-nav-dot", dotTo: "/crm/pool?assign=10", dotTitle: zh ? `${poolWaiting} 位客户待分配 · 点击一键按规则分配最早的 10 位` : `${poolWaiting} waiting · click to assign the 10 oldest by rule` },
        { to: "/crm/lost-analytics", icon: TrendingDown, label: zh ? "流失分析" : "Churn Analysis", def: isLostAnalytics },
        { to: "/crm/overdue", icon: AlertTriangle, label: zh ? "超期看板" : "Overdue Board", def: ["sales_manager", "sales_vp", "marketing_manager"].includes(user.role) || isAdmin || isCeo },
      ] },
      { sub: "calls", icon: Headphones, label: zh ? "电话营销" : "Telemarketing", items: [
        { to: "/outbound-list", icon: PhoneOutgoing, label: zh ? "智能外呼清单" : "Outbound List", def: isCrm || isAdmin || isCeo },
        { to: "/golden-scripts", icon: BookOpen, label: zh ? "金牌话术库" : "Golden Scripts", def: isCrm || isAdmin || isCeo || isTm },
        { to: "/agents", icon: Headphones, label: zh ? "坐席看板" : "Agent Board", def: ["sales_manager", "sales_vp", "marketing", "tm_manager"].includes(user.role) || isAdmin || isCeo },
        { to: "/call-analytics", icon: BarChart3, label: zh ? "话务分析" : "Call Analytics", def: ["sales_manager", "sales_vp", "marketing", "tm_manager"].includes(user.role) || isAdmin || isCeo },
        { to: "/tm/workbench", icon: LayoutDashboard, label: zh ? "电销工作台" : "TM Workbench", def: isTmView || isTm },
        { to: "/tm/today", icon: PhoneCall, label: zh ? "今日智能呼出" : "Today's Calls", def: isTm || ["sales_manager", "sales_vp", "marketing_manager"].includes(user.role) || isAdmin || isCeo },
        { to: "/tm/tasks", icon: ListChecks, label: zh ? "任务分配" : "Task Assignment", def: isTmView || isTm },
        { to: "/tm/my-leads", icon: UserCheck, label: zh ? "我的潜在客户" : "My Leads", def: isTm || ["sales_manager", "sales_vp", "marketing_manager", "marketing"].includes(user.role) || isAdmin || isCeo },
        { to: "/tm/b2b", icon: Handshake, label: zh ? "经纪人 B2B 漏斗" : "Realtor B2B Pipeline", def: ["tm_manager", "b2b_sales", "marketing", "marketing_manager", "sales_vp", "sales_manager", "telesales"].includes(user.role) || isAdmin || isCeo },
        { to: "/tm/funnel", icon: Filter, label: zh ? "潜客转化看板" : "Conversion Funnel", def: ["tm_manager", "marketing", "marketing_manager", "sales_vp", "sales_manager", "telesales"].includes(user.role) || isAdmin || isCeo },
        { to: "/tm/qa", icon: Gauge, label: zh ? "通话质检看板" : "Call QA Board", def: ["tm_manager", "marketing", "marketing_manager", "sales_vp", "sales_manager", "telesales", "b2b_sales"].includes(user.role) || isAdmin || isCeo },
        { to: "/tm/homeowners", icon: Home, label: zh ? "屋主名单库" : "Homeowner Database", def: isTmView || user.role === "telesales" },
        { to: "/tm/realtors", icon: Building2, label: zh ? "地产经纪人名单库" : "Realtor Database", def: isTmView || isTm },
        { to: "/tm/imports", icon: Upload, label: zh ? "导入记录" : "Import Records", def: ["tm_manager", "data_importer", "marketing_manager"].includes(user.role) || isAdmin || isCeo },
        { to: "/tm/invalid", icon: AlertTriangle, label: zh ? "无效名单 / DNC" : "Invalid / DNC", def: ["tm_manager", "data_importer", "marketing_manager"].includes(user.role) || isAdmin || isCeo },
        { to: "/tm/settings", icon: Target, label: zh ? "标签与规则设置" : "Tags & Rules", def: user.role === "tm_manager" || isAdmin },
        { to: "/tm/audit", icon: ScrollText, label: zh ? "电销操作日志" : "TM Audit Log", def: ["tm_manager", "marketing_manager"].includes(user.role) || isAdmin || isCeo },
      ] },
    ] },
    { key: "design", label: zh ? "设计部" : "Design Dept", items: [
      { to: "/design/dashboard", icon: LayoutDashboard, label: zh ? "总览仪表盘" : "Dashboard", def: isDesign || isDesignMgr || isCeo },
      // 需求（FB37b23108c6）：销售 VP 要能主动进「设计部 › 项目管理」跟每个 sales 跟进设计和项目，
      // 而不是只在项目里被 @ 之后才被动看到单个项目。菜单可见性与后端 MODULE_CATALOG 的
      // "/design/projects" 默认角色保持同源（同样加了 sales_vp），避免「菜单进得去、接口 403」。
      { to: "/design/projects", icon: Building2, label: zh ? "项目管理" : "Projects", def: isDesign || isDesignMgr || isCeo || user.role === "sales_vp" },
      // 2026-10-20 用户需求：销售要在「签约前营销方案」里创建任务、设截止时间、发起待改进，
      // 因此该菜单必须对销售开放（原先只有设计岗 / 设计经理 / CEO 能看到，
      // 销售只能绕回 CRM 客户详情，等于模块内无法创建）。
      // 角色清单必须与后端 design_presign_orders.SALES_ROLES 完全同源：少一个会「有接口权限却没入口」，
      // 多一个会「看得到菜单、点进去 403」。注意 marketing_manager 不在后端 SALES_ROLES 里，
      // 所以这里不能直接复用 isCrm（它包含 marketing_manager）。
      { to: "/design/presign", icon: Megaphone, label: zh ? "签约前营销方案" : "Pre-contract Concepts",
        def: isDesign || isDesignMgr || isCeo || ["sales", "sales_manager", "sales_vp", "marketing", "eng_manager"].includes(user.role) },
      { to: "/design/tasks", icon: ClipboardCheck, label: zh ? "待办任务" : "Tasks", def: isDesign || isDesignMgr },
      // 需求 FB599d1f7b7b：销售副总本来就能在后端审批暂停 / 取消 / 延期项目，
      // 但菜单只对设计岗位开放，导致 Alice / Jacky 在提醒里只能查看项目、找不到审批入口。
      // 可见角色与后端 /design/approvals 模块目录和 EXEC_APPROVERS 对齐，不改任何审批权限。
      { to: "/design/approvals", icon: ShieldCheck, label: zh ? "审批中心" : "Approvals", def: isDesign || isDesignMgr || user.role === "sales_vp" },
      { to: "/design/sign-dates", icon: FileSignature, label: zh ? "签约日期设置" : "Sign Dates", def: isDesignMgr || user.role === "sales_vp" },
      { to: "/design/team", icon: Users, label: zh ? "团队分工" : "Team", def: isDesign || isDesignMgr || isCeo },
      { to: "/design/automation", icon: Workflow, label: zh ? "自动化中心" : "Automation", def: isDesignMgr },
      { to: "/design/ai", icon: Bot, label: zh ? "AI 管理助手" : "AI Assistant", def: isDesign || isDesignMgr || isCeo },
      { to: "/design/settings", icon: Plug, label: zh ? "集成与设置" : "Integrations", def: isDesignMgr },
      { to: "/design/reports", icon: BarChart3, label: zh ? "绩效报表" : "Reports", def: isDesign || isDesignMgr || isCeo },
    ] },
    { key: "eng", label: zh ? (isSales ? "工程进度" : "工程管理") : (isSales ? "Construction" : "Construction Mgmt"), items: [
      // 【2026-10-22 补 isPm】原 def 漏了项目经理，pm 账号侧栏没有「工程项目」入口，
      // 点 /board 会被代操作守卫弹回默认首页（/change-orders），市检日历上的「添加市检」
      // 按钮因此永远看不到。后端 can_access_project 与市检 MANAGER_ROLES 都已包含 pm，
      // 这里补上让三处口径一致（与 server.py MODULE_CATALOG 的 /board 同步）。
      { to: "/board", icon: KanbanSquare, label: isSales ? (zh ? "我的客户施工进度" : "My Clients' Projects") : isBranchView ? (zh ? "本分公司在建项目" : "Branch Projects") : t("teamBoard"), def: isAdmin || isMember || isEngMgr || isCdc || isSales || isBranchView || isPm, dot: isSales ? constructionUnread : kickoffCount, dotTestid: isSales ? "construction-nav-dot" : "kickoff-nav-dot", dotTitle: isSales ? undefined : (zh ? `${kickoffCount} 个新项目待开工 · 点击查看` : `${kickoffCount} project(s) awaiting kickoff`) },
      { to: "/projects/map", icon: MapIcon, label: zh ? "项目地图" : "Project Map", def: isAdmin || isMember || isEngMgr || isCeo || isPm || isCdc || isBranchSalesMgr },
      /* 二-7：整改看板(/rectify)与异常看板(/engine-exceptions)已删除。
         老板原话（09:28-10:04）：这两个板块不知道是干什么的，设计逻辑没搞清楚，
         这块没有生效，那两个我没看过，有可能要删掉。 */
      /* 二-8：超期预警看板已删除。老板原话（10:09-10:23）：超期预警在工程项目页下也会显示，
         这个看板是重复功能，可以删了。路由和页面文件见 App.js / OverdueBoard.jsx。 */
      /* 二-9：「全链路运行状态」看板已删除。老板原话（10:20-10:41）：点开每个项目详情
         本来就会显示全链路，这个看板没有很大必要，而且它还有前置条件限制。 */
      { to: "/online-pm", icon: FileStack, label: zh ? "线上项目经理工作台" : "Online PM Workbench", def: isAdmin || isEngMgr || isCeo || isPm || isCdc },
      { to: "/sub-checkin-board", icon: ClipboardList, label: zh ? "分包今日报工" : "Sub Check-ins", def: isAdmin || isEngMgr || isCeo || isPm || isCdc || isMember },
      { to: "/change-orders", icon: FileEdit, label: zh ? "变更订单" : "Change Orders", def: isAdmin || isCeo || isFinance || isEngMgr || isSales || isPm || isBranchSalesMgr },
      { to: "/subcontractor-companies", icon: Building2, label: zh ? "分包商公司库" : "Subcontractors", def: isAdmin || isEngMgr || isCeo || isPm || isMember || isCdc || isFinance || isCashier },
      { to: "/archive", icon: Archive, label: zh ? "完工项目" : "Completed", def: isAdmin || isMember || isEngMgr || isCeo || isFinance || isSales || isPm || isCdc || isBranchView },
      // 需求：材料采购紧跟完工项目显示；沿用原路由和权限键，保留既有授权、收藏与提醒链接。
      // CEO 待审批徽标仍取独立计数接口，移动菜单不改变申请与审批流程。
      { to: "/temp-material-procurement", icon: ClipboardCheck, label: zh ? "材料采购" : "Material Procurement", def: isAdmin || isCeo || isFinance || isEngMgr || isPm || isMember || isWhBuyer, dot: isCeo ? procurementPendingCount : 0, dotTestid: "temp-material-procurement-pending-dot", dotTitle: procurementPendingCount ? (zh ? `${procurementPendingCount} 笔采购待 CEO 审批` : `${procurementPendingCount} procurement request(s) awaiting CEO approval`) : undefined },
    ] },
    { key: "wh", label: zh ? "仓储物流管理" : "Warehouse & Logistics", items: [
      { to: "/warehouse-dashboard", icon: LayoutDashboard, label: zh ? "仓储看板" : "Dashboard", def: isWhRole || isAdmin || isEngMgr || isCeo || isMember || isFinance },
      { to: "/inventory", icon: Boxes, label: zh ? "材料库存" : "Inventory", def: isWhMgr || isWhBuyer || isAdmin || isEngMgr || isCeo || isPm || isCdc || isMember || isFinance },
      { to: "/warehouses", icon: Warehouse, label: zh ? "仓库管理" : "Warehouses", def: isWhRole || isAdmin || isEngMgr || isCeo || isMember || isFinance },
      { to: "/warehouses/scan", icon: ScanLine, label: zh ? "扫码工作台" : "Scan Station", def: isWhMgr || isWhBuyer || isAdmin || isEngMgr || isCeo || isMember },
      { to: "/warehouse-audit", icon: ScrollText, label: zh ? "仓库操作流水" : "WH Ledger", def: isWhAudit || isAdmin || isEngMgr },
    ] },
    { key: "fin", label: zh ? "财务管理" : "Finance", items: [
      { to: "/finance-center", icon: Wallet, label: zh ? "财务中心" : "Finance Center", def: isAdmin || isFinance || isCeo || isEngMgr },
      { to: "/finance/projects", icon: HardHat, label: zh ? "项目财务" : "Project Finance", def: isAdmin || isFinance || isCeo || isEngMgr },
      { to: "/finance/payees", icon: UsersRound, label: zh ? "收款方总账" : "Payee Ledger", def: isAdmin || isFinance || isCeo || isEngMgr },
      { to: "/finance/funds", icon: Landmark, label: zh ? "资金管理" : "Funds (AR/AP)", def: isAdmin || isFinance || isCeo },
      { to: "/crm/payment-calendar", icon: CalendarDays, label: zh ? "收款日历" : "Payment Calendar", def: isFinance, key: "fin-payment-calendar" },
      { to: "/finance/batch-collect", icon: Megaphone, label: zh ? "批量催收" : "Batch Collection", def: isFinance || isAdmin || isCeo },
      { to: "/payments", icon: Banknote, label: zh ? "工程支付" : "Payments", def: isAdmin || isFinance || isEngMgr || isCashier || isCeo },
    ] },
    { key: "hr", label: zh ? "人力资源" : "Human Resources", items: [
      { to: "/announcements", icon: Megaphone, label: zh ? "公司公告" : "Announcements", def: true },
      { to: "/hrms/talent", icon: UsersRound, label: zh ? "人才盘点" : "Talent Review", def: isHrFull },
      { to: "/hrms/me", icon: CircleUserRound, label: zh ? "我的 HR" : "My HR", def: !isSub },
      { to: "/hrms/my-schedule", icon: CalendarDays, label: zh ? "我的排班" : "My Schedule", def: !isSub },
      { to: "/finance/my-card", icon: CreditCard, label: zh ? "我的信用卡" : "My Credit Card", def: !isSub && myCardCount > 0, when: !isSub && myCardCount > 0 },
      { sub: "hr-org", icon: Network, label: zh ? "组织与规划" : "Org & Planning", items: [
        { to: "/hrms/org", icon: Network, label: zh ? "组织架构" : "Organization", def: isHrFull },
        { to: "/hrms/planning", icon: TrendingUp, label: zh ? "人力规划" : "Workforce Planning", def: isHrFull },
        { to: "/hrms", exact: true, icon: BarChart3, label: zh ? "数据仪表盘" : "HR Dashboard", def: isHrFull },
      ] },
      { to: "/hrms/recruiting", icon: UserPlus, label: zh ? "招聘 ATS" : "Recruiting ATS", def: isHrFull },
      // 【2026-10-27 改版 2a】本分公司人员（经理端人员与账号管理页）已并入统一权限体系：
      // 它就是权限目录里的一个普通页面权限点，可见性由权限快照判定（与其他页面同一份判定）。
      // 默认 def=false 不是遗漏：目录默认角色为空，没人默认可见 —— 管理员在权限矩阵 /
      // 账号勾选树里勾给谁，谁的侧栏才出现这个入口（数据范围取账号自身的分公司）。
      { to: "/branch-staff", icon: UserPlus, label: zh ? "本分公司人员" : "My Branch Staff", def: false },
      { to: "/hrms/employees", icon: IdCard, label: zh ? "员工档案" : "Employees", def: isHrFull, divider: zh ? "员工管理" : "Employee Management" },
      { to: "/hrms/assets", icon: Boxes, label: zh ? "固定资产" : "Company Assets", def: isHrFull, divider: zh ? "员工管理" : "Employee Management" },
      { to: "/hrms/schedule", icon: CalendarDays, label: zh ? "考勤排班" : "Weekly Schedule", def: isHrFull || isDeptMgr, divider: isHrFull ? undefined : (zh ? "员工管理" : "Employee Management") },
      { to: "/hrms/attendance", icon: Clock, label: zh ? "考勤打卡与假勤" : "Attendance & Leave", def: isHrFull },
      { to: "/hrms/compensation", icon: Banknote, label: zh ? "薪酬激励" : "Compensation", def: isHrFull },
      { to: "/hrms/benefits", icon: Gift, label: zh ? "福利管理" : "Benefits", def: isHrFull },
      { to: "/hrms/cn-payroll", icon: Landmark, label: zh ? "中国薪酬社保" : "China Payroll", def: isAdmin || isCeo || ((user.role === "hr" || user.role === "finance") && user.region === "cn") },
      { to: "/hrms/us-payroll", icon: Calculator, label: zh ? "美国薪资计算" : "US Payroll", def: isAdmin || isCeo || ((user.role === "hr" || user.role === "finance") && user.region !== "cn") },
      { to: "/hrms/onboarding", icon: ClipboardList, label: zh ? "入职表单 W-4/I-9" : "Onboarding Packets", def: isAdmin || isCeo || user.role === "hr" },
      { to: "/hrms/performance", icon: Target, label: zh ? "绩效考核" : "Performance", def: isHrFull },
      { to: "/hrms/relations", icon: Scale, label: zh ? "员工关系" : "Relations", def: isHrFull, divider: zh ? "员工安全与培训" : "Safety & Training" },
      { to: "/hrms/safety", icon: LifeBuoy, label: zh ? "安全与工伤" : "Safety & WC", def: isHrFull },
      { to: "/hrms/training", icon: GraduationCap, label: zh ? "培训发展" : "Training", def: isHrFull },
    ] },
    { key: "collab", label: zh ? "协作 / 工具" : "Collaboration", items: [
      { to: "/collab", icon: Handshake, label: zh ? "协作任务" : "Collab Requests", def: !isSub, dot: collabInbox },
      // FBf71948367e：知识库入口由 /knowledge 权限快照控制；部门和文件范围由后端按账号部门隔离。
      { to: "/knowledge", icon: BookOpen, label: zh ? "部门知识库" : "Department Knowledge", def: false },
      { to: "/chat", icon: MessagesSquare, label: zh ? "协作聊天" : "Chat Hub", def: !isSub, dot: chatUnread, dotTestid: "chat-nav-dot" },
      // 【2026-10-28 修复】「提醒中心」原先写死一份岗位名单（admin / 财务 / 工程 / 仓库 / cdc / pm），
      // 于是销售、设计、HR 等账号在侧栏看不到入口；2026-10-24 权限同源化后，连顶栏铃铛的
      // 「查看全部提醒 →」也会被 pathDenied 拦成「无权访问该模块」（线上反馈：每日待办没办法处理，
      // 右上角提醒没有权限）。现在与同组 /todos /chat 同口径：!isSub，真正的判定交给
      // 服务端权限快照（后端 MODULE_CATALOG 已把 /reminders 登记为 "*" 全员自助页），
      // 管理员显式屏蔽某账号时快照仍会返回 false，入口随之收起 —— 不再在前端重复一套名单。
      // 分包商依旧隐藏：他们的提醒在分包门户的施工日程里处理。
      { to: "/reminders", icon: Bell, label: t("reminders"), def: !isSub },
      { to: "/my-mentions", icon: AtSign, label: zh ? "待读汇总" : "@ Follow-ups", def: !isSub, dot: myUnread },
      { to: "/calls", icon: PhoneCall, label: zh ? "通话记录" : "Call History", def: !isSub },
    ] },
    { key: "portal", label: zh ? "分包协作" : "Subcontractor", items: [
      // 项目经理和线上项目经理需进入月历派工；账号代操作仍由工作台独立检查权限。
      // 分包商侧栏分为三个业务菜单，子菜单沿用工作台权限；管理人员仍用原派工入口。
      { to: "/portal", exact: isSub, icon: Briefcase, label: isSub ? (zh ? "信息与合约管理" : "Company & contracts") : (zh ? "分包商工作台" : "Subcontractor Portal"), def: isSub || isAdmin || isCeo || isEngMgr || isPm || isCdc },
      ...(isSub && vis("/portal", true) ? [
        { to: "/portal/schedule", icon: CalendarDays, label: zh ? "施工日程日历" : "Construction calendar", def: true },
        { to: "/portal/progress", icon: Banknote, label: zh ? "项目进度与付款申请" : "Progress & payments", def: true },
      ] : []),
    ] },
    { key: "admin", label: zh ? "系统管理" : "Admin", items: [
      // 发布前核对属于系统管理，移出工程菜单；桌面、手机与面包屑共用本定义。
      // 只调整入口分组，沿用原路径与可见角色，保持既有授权、收藏链接可用。
      { to: "/admin/pre-publish", icon: ClipboardCheck, label: zh ? "发布前核对" : "Pre-publish Check", def: isAdmin || isEngMgr || isCeo || isPm || isCdc || isFinance },
      { to: "/audit-log", icon: ScrollText, label: zh ? "审计日志" : "Audit Log", def: isAdmin || isCeo || isFinance || isEngMgr },
      { to: "/quickbooks", icon: Plug, label: "QuickBooks", def: isAdmin },
      { to: "/import", icon: Upload, label: t("dataImport"), def: isAdmin },
      // 【2026-10-13 新增】「用户管理」二级折叠组：原「账号权限」单项拆为「角色管理」+「账号列表」，
      // 与后端 MODULE_CATALOG 的 sub=("用户管理","User Management") 分组一一对应。
      // 侧栏与权限目录必须同步（否则菜单看得见但矩阵里勾不到），有自检脚本 backend/tests/qa_nav_catalog.py。
      //
      // 【2026-10-21 调整】「使用分析」由系统管理下的平级项移入本组，排在「账号列表」之后。
      // 为什么这么放：使用分析看的就是账号的逐人使用轨迹，与账号列表同属「人」的管理视角；
      // 它原本平铺在系统管理里与审计日志、QuickBooks 等 IT 配置项混排，语义上不归位。
      // 可见范围保持不变（入口位置 ≠ 数据范围）：admin / CEO 看到本组三项，
      // 各条线经理只看到「使用分析」，能看多少数据仍由后端 usage_scope 收窄。
      // 注意：本组的分组标题固定为「用户管理」，经理那侧只出现单项是预期结果，
      // 不要为此改成动态标题 —— 同一入口在不同账号下换名字会让人对不上文档与培训材料。
      { sub: "user-mgmt", icon: UsersRound, label: zh ? "用户管理" : "User Management", items: [
        { to: "/admin/roles", icon: ShieldCheck, label: zh ? "角色管理" : "Roles & Permissions", def: isAdmin },
        { to: "/admin/users", icon: Users, label: zh ? "账号列表" : "Accounts", def: isAdmin },
        // 默认开放给 CEO 与各条线经理：菜单位置只是「入口」，能看多少数据由
        // usage_analytics.usage_scope 收窄（经理只本部门 / 本分公司，普通员工只自己）。
        // 与后端 MODULE_CATALOG 的 /admin/usage 默认角色保持一致，改一处要同步另一处
        // （自检脚本 backend/tests/qa_nav_catalog.py 会检查两侧路由是否对齐）。
        { to: "/admin/usage", icon: Activity, label: zh ? "使用分析" : "Usage Analytics", def: isAdmin || isCeo || isUsageViewer },
      ] },
      { to: "/admin/branches", icon: Building2, label: zh ? "分公司管理" : "Branches", def: isAdmin },
      { to: "/admin/menus", icon: Menu, label: zh ? "菜单管理" : "Menu Management", def: isAdmin },
      { to: "/admin/migration", icon: ArrowLeftRight, label: zh ? "数据迁移包" : "Data Migration", def: isAdmin },
      { to: "/admin/i18n", icon: Globe, label: zh ? "英文译文词典" : "English Dictionary", def: isAdmin || isCeo },
      // 【2026-10-14 新增】需求反馈汇总页（仅 admin/ceo）。角标 = 未读反馈数，点开管理页即清零。
      { to: "/admin/feedback", icon: MessageSquarePlus, label: zh ? "需求反馈" : "Feedback", def: isAdmin, dot: fbAdminUnread, dotTestid: "feedback-admin-nav-dot", dotTitle: zh ? `${fbAdminUnread} 条反馈未读 · 点击查看` : `${fbAdminUnread} unread feedback` },
      { to: "/admin/owner-sms", icon: MessageSquareText, label: zh ? "通信设置" : "Communication Settings", def: isAdmin || isCeo },
      { to: "/admin/ai", icon: Bot, label: zh ? "AI 配置" : "AI Settings", def: isAdmin || isCeo },
      { to: "/admin/google-maps", icon: MapPinned, label: zh ? "地图配置" : "Google Maps", def: isAdmin || isCeo },
      // 【2026-10-26 新增】Google 日历 OAuth 凭据（HRMS 走查一-2）。与地图配置、通信设置同档：
      // 都是全公司级外部集成凭据，只给 admin / ceo。后端 MODULE_CATALOG 有同名登记，两处必须同步
      // （backend/tests/qa_nav_catalog.py 会校验侧栏路由是否都能在权限目录里找到）。
      { to: "/admin/google-calendar", icon: CalendarClock, label: zh ? "Google 日历" : "Google Calendar", def: isAdmin || isCeo },
      // 现金管理只向管理员 / CEO 构造入口，普通账号即使被误授菜单权限也不会看到。
      ...(isAdmin ? [{ to: "/admin/cash-mode", icon: ShieldCheck, label: zh ? "现金功能" : "Cash Settings", def: true }] : []),
      { to: "/admin/sms-consent", icon: MessageSquareText, label: zh ? "短信同意记录" : "SMS Consent Log", def: isAdmin || isCeo },
      { to: "/crm/automations", icon: Workflow, label: zh ? "CRM 自动化" : "CRM Automations", def: isAdmin || isCeo },
      { to: "/admin/trade-pricing", icon: Calculator, label: zh ? "工种定价公式" : "Trade pricing", def: isAdmin || isEngMgr },
      { to: "/admin/nav-places", icon: MapIcon, label: zh ? "导航常用出发地" : "Navigation start points", def: isAdmin || isEngMgr || isCeo },
      { to: "/recycle-bin", icon: Trash2, label: zh ? "回收站" : "Recycle Bin", def: isAdmin || isEngMgr || isCeo || user.role === "sales_vp" },
    ] },
    { key: "not-yet-available", label: zh ? "暂不开放" : "Not Yet Available", defaultOpen: false, items: [
      { to: "/crm/contracts/unsigned", icon: FileSignature, label: zh ? "合同签署提醒" : "Signing Reminders", def: (isCrm && user.role !== "marketing_manager") || isAdmin || isCeo, dot: unsignedOver7, dotTestid: "unsigned-over7-nav-dot" },
      { to: "/crm/assistant-log", icon: UserCog, label: zh ? "助理代操作记录" : "Assistant Actions", def: (isSales && (user.assistants || []).length > 0) || ["sales_manager", "sales_vp"].includes(user.role) || isAdmin || isCeo,
        // 【2026-10-24】纯运行时条件拆出（快照判定的是「角色/授权」，这里补「是否有助理」）：
        // 销售本人无助理时不上该入口；其它目录内角色由快照判定。
        when: !isSales || (user.assistants || []).length > 0 },
      { to: "/crm/eval-invites", icon: CalendarClock, label: zh ? "现场评估邀约" : "Eval Invites", def: evalInvites.total > 0 || isCrm || isAdmin || isCeo, dot: evalInvites.pending, dotTestid: "eval-invites-nav-dot" },
      { to: "/crm/price-approvals", icon: ShieldCheck, label: zh ? "改价审批" : "Price Approvals", def: isApprover, dot: pendingApprovals, dotTestid: "price-approvals-nav-dot" },
      { to: "/crm/mm-review", icon: ClipboardCheck, label: zh ? "待复核" : "MM Review", def: isMMReviewer, dot: mmReviewCount, dotTestid: "mm-review-nav-dot" },
      { to: "/satisfaction", icon: Star, label: zh ? "满意度看板" : "Satisfaction", def: isAdmin || isEngMgr || isCeo },
      { to: "/site-inspections", icon: HardHat, label: zh ? "现场巡检总览" : "Site Inspections", def: isAdmin || isEngMgr || isCeo || isPm || isCdc || isMember },
      { to: "/sign-followup", icon: FileSignature, label: zh ? "合同签署跟进" : "Contract Signing", def: isAdmin || isEngMgr || isCeo || isPm || isCdc || isFinance },
      { to: "/finance/budget-defaults", icon: Percent, label: zh ? "预算比例设置" : "Budget Ratios", def: isAdmin || isFinance || isCeo || isEngMgr },
    ] },
  ];
  const flatNav = NAVDEF.flatMap((g) => g.items.flatMap((it) => (it.sub ? it.items : [it])));
  flatNavRef.current = flatNav;
  const favItems = favs.map((to) => flatNav.find((it) => it.to === to)).filter((it) => it && vis(it.to, it.def, it.when));
  const TOUR_STEPS = [
    { target: '[data-testid="sidebar-brand"], [data-testid="sidebar-brand-mark"]', title: lang === "zh" ? "欢迎使用全新界面" : "Welcome to the new UI", body: lang === "zh" ? "Aligcus 换上了更清爽的浅色蓝紫设计。侧栏现在常驻左侧，所有模块一目了然。" : "Aligcus now wears a lighter blue-violet design. The sidebar stays put on the left so every module is one click away." },
    { target: '[data-testid="sidebar-collapse-toggle"]', title: lang === "zh" ? "收起 / 展开侧栏" : "Collapse the sidebar", body: lang === "zh" ? "点这里把侧栏折叠成窄图标栏，给报表和看板留出更多空间；再点一次展开。" : "Click to fold the sidebar into a slim icon rail for more room; click again to expand." },
    { target: '[data-testid="sidebar-nav"] a', title: lang === "zh" ? "★ 收藏常用页面" : "★ Pin favorites", body: lang === "zh" ? "把鼠标悬停在任一菜单上，点右侧的星星即可把它固定到顶部「常用」分组（最多 6 个，跨设备同步）。" : "Hover any menu item and click the star to pin it to the “Pinned” group at the top (up to 6, synced to your account)." },
    { target: '[data-testid="topbar-lang-toggle"]', title: lang === "zh" ? "中 / EN / ES 一键切换" : "Switch ZH / EN / ES", body: lang === "zh" ? "整站中文、英文、西班牙语随时切换，偏好会记住。" : "Toggle the whole workspace between Chinese, English and Spanish anytime." },
    /* 全局搜索（2026-10-20 恢复跨模块）：SYS-002 时期曾收窄成「只找页面」，SYS-006 反馈搜不到客户后
       又放开，并改为按账号权限返回客户 / 工程项目 / 设计项目 / 频道 / 同事。引导文案同步讲清这一点。 */
    { target: '[data-testid="global-search-btn"]', title: lang === "zh" ? "⌘K 全局搜索" : "⌘K global search", body: lang === "zh" ? "按 ⌘K / Ctrl+K 或点这里，输入客户姓名 / 电话 / 项目地址 / 同事姓名直接跳转；只显示你有权限的内容，项目会按岗位跳到工程项目或设计项目详情。" : "Press ⌘K / Ctrl+K or click here to jump to a customer, project address or colleague. Results follow your permissions; projects open in the engineering or design detail page depending on your role." },
    { target: '[data-testid="theme-toggle"]', title: lang === "zh" ? "外观与密度" : "Appearance & density", body: lang === "zh" ? "浅色 / 深色 / 跟随系统 / 定时切换深海军蓝主题；紧凑模式让小屏笔记本一屏看更多表格行。" : "Light / dark / system / scheduled navy theme, plus a compact density for small laptops." },
    { target: '[data-testid="topbar-bell"]', title: lang === "zh" ? "提醒中心" : "Notifications", body: lang === "zh" ? "逾期、审批、提及和系统提醒都在这里，支持一键处理。" : "Overdue items, approvals, mentions and system alerts live here with one-click actions." },
    { target: '[data-testid="tour-help-btn"]', title: lang === "zh" ? "随时重看引导" : "Replay anytime", body: lang === "zh" ? "点顶栏的「?」可以再次播放这段引导。祝工作顺利！" : "Click ? in the top bar to replay this tour. Enjoy!" },
  ];

  const navVis = (to) => { const it = flatNav.find((x) => x.to === to); return it ? vis(it.to, it.def, it.when) : !(pathDenied && pathDenied(to)); };
  // 管理页路由守卫：/admin/* 下、且侧栏对该账号不可见的页面，直接拒绝渲染（后端接口另有各自 403 兜底）。
  // 只限 /admin/ 前缀 —— 全局按导航可见性拦截会误伤不在侧栏的页面（如 /reminders 铃铛对所有人可达）。
  const _adminNavItem = flatNav.find((x) => x.to === location.pathname);
  const adminPathBlocked = !!(_adminNavItem && location.pathname.startsWith("/admin/") && !vis(_adminNavItem.to, _adminNavItem.def, _adminNavItem.when));
  const landingOptions = flatNav.filter((it) => vis(it.to, it.def, it.when)).map((it) => ({ to: it.to, label: it.label }));
  // 手机高频入口与完整菜单共用 vis()，避免把无权限的工程页展示给销售/分包商。
  const mobileWork = [
    { to: "/board", label: zh ? "项目" : "Projects" },
    { to: "/crm/leads", label: zh ? "客户" : "Customers" },
    { to: isSub ? "/portal/schedule" : "/portal", label: zh ? "施工日程" : "Schedule" },
    { to: "/hrms/my-schedule", label: zh ? "排班" : "Schedule" },
  ].map((option) => ({ ...flatNav.find((it) => it.to === option.to), ...option }))
    .filter((it) => it.icon && vis(it.to, it.def, it.when)).slice(0, 1);
  // 把「当前账号可见的导航路径」发布给 Landing 做校验：换账号后不会落到上个账号存的、自己无权限的登录首页
  setAllowedNavPaths(landingOptions.map((o) => o.to));

  // 展开时版本紧跟品牌名称，收起时放在图标下方。
  // 仍显示当前页面构建时的版本；用户选择稍后刷新时不能误显示服务器上的新版。
  const sidebarVersion = (
    <span data-testid="sidebar-build-version" title={lang === "zh" ? "当前系统版本" : "Current system version"}
      className={`${rail && !mobileNav ? "mt-0.5" : ""} block max-w-full truncate text-[11px] leading-4 font-medium text-slate-600 dark:text-slate-300`}>
      {process.env.REACT_APP_RELEASE_LABEL || (lang === "zh" ? "开发版" : lang === "es" ? "Desarrollo" : "Development")}
    </span>
  );

  return (
    <RailCtx.Provider value={rail}>
    <FavCtx.Provider value={{ favs, toggleFav }}>
    <div className="app-shell min-h-screen flex ambient-glow">
      {/* Sidebar */}
      <ResponsiveSidebar open={mobileNav} onOpenChange={setMobileNav} zh={zh}>
      <aside data-testid="app-sidebar" data-rail={rail ? "1" : "0"}
        className={`app-sidebar ${rail ? "w-[76px]" : "w-64"} bg-white border-r border-slate-200/80 flex flex-col fixed inset-y-0 left-0 z-40 transform transition-[width,transform] duration-200 ${mobileNav ? "translate-x-0 w-64 shadow-2xl shadow-slate-900/20" : "-translate-x-full"} lg:translate-x-0`}>
        <div className={`flex items-center ${rail && !mobileNav ? "flex-col justify-center px-0" : "px-4"} h-16 border-b border-slate-100 shrink-0`}>
          {rail && !mobileNav
            ? <><BrandMark size={34} testid="sidebar-brand-mark" />{sidebarVersion}</>
            : <BrandLogo size={36} tagline={lang === "zh" ? "阿里建筑 · AI 管理系统" : "Ali Construction · AI Platform"} supportingContent={sidebarVersion} testid="sidebar-brand" />}
          <button aria-label={zh ? "关闭菜单" : "Close menu"} className="ml-auto lg:hidden h-11 w-11 shrink-0 flex items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100" data-testid="mobile-nav-close" onClick={() => setMobileNav(false)}><X className="h-5 w-5" /></button>
        </div>
        <div className="lg:hidden px-3 pt-3 shrink-0">
          <input type="search" value={mobileMenuQuery} onChange={(e) => setMobileMenuQuery(e.target.value)}
            aria-label={zh ? "筛选功能菜单" : "Filter menu"} placeholder={zh ? "搜索功能，如：排班、财务" : "Find a feature…"}
            className="w-full min-h-11 rounded-xl border border-slate-200 bg-slate-50 px-3 text-base" />
        </div>
        <nav className={`flex-1 min-h-0 overflow-y-auto ${rail && !mobileNav ? "px-2.5" : "px-3"} py-3 sidebar-scroll`} data-testid="sidebar-nav" onClick={(e) => { if (e.target.closest("a")) setMobileNav(false); }}>
          <RailCtx.Provider value={rail && !mobileNav}>
          {favItems.length > 0 && !mobileMenuQuery && (
            <NavGroup label={lang === "zh" ? "常用" : "Pinned"}>
              {favItems.map((it, i) => {
                const expanded = !(rail && !mobileNav);
                return (
                  <div key={`fav-${it.to}`} data-testid={`fav-row-${i}`} data-path={it.to}
                    draggable={expanded} onDragStart={(e) => { dragIdx.current = favs.indexOf(it.to); e.dataTransfer.effectAllowed = "move"; }}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(i); }} onDragLeave={() => setDragOver(null)}
                    onDrop={(e) => { e.preventDefault(); moveFav(dragIdx.current, favs.indexOf(it.to)); dragIdx.current = null; setDragOver(null); }} onDragEnd={() => { dragIdx.current = null; setDragOver(null); }}
                    className={`group/fav relative rounded-xl transition-shadow ${dragOver === i ? "ring-2 ring-brand-300" : ""}`}>
                    {expanded && <GripVertical className="absolute -left-1 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-300 opacity-0 group-hover/fav:opacity-100 cursor-grab" data-testid={`fav-drag-${i}`} />}
                    <NavItem to={it.to} exact={it.exact} icon={it.icon} label={it.label} dot={it.dot} dotTestid={it.dotTestid ? `fav-${it.dotTestid}` : "fav-nav-dot"} hint={`${isMac ? "⌥" : "Alt+"}${i + 1}`} testidPrefix="fav-link" />
                  </div>
                );
              })}
            </NavGroup>
          )}
          {NAVDEF.map((g) => {
            // 手机菜单搜索同时匹配名称与路径；仍先检查角色权限，不能搜出被隐藏的模块。
            const matches = (it) => !mobileMenuQuery.trim() || `${it.label} ${it.to} ${g.label}`.toLocaleLowerCase().includes(mobileMenuQuery.trim().toLocaleLowerCase());
            const items = g.items
              .map((it) => (it.sub ? { ...it, items: it.items.filter((c) => vis(c.to, c.def, c.when) && matches(c)) } : it))
              .filter((it) => (it.sub ? it.items.length > 0 : vis(it.to, it.def, it.when) && matches(it)));
            if (!items.length) return null;
            const hasCurrentPage = g.defaultOpen === false && g.items.some((it) => it.to && (location.pathname === it.to || (!it.exact && location.pathname.startsWith(`${it.to}/`))));
            return (
              <NavGroup key={g.key} label={g.label} defaultOpen={g.defaultOpen !== false}
                railIcon={g.key === "not-yet-available" ? Archive : undefined} railActive={hasCurrentPage}
                forceOpen={!!mobileMenuQuery || (!!navFocusPath && g.items.some((it) => it.sub ? it.items.some((c) => c.to === navFocusPath) : it.to === navFocusPath)) || hasCurrentPage}>
                {items.map((it) => it.sub ? (
                  <NavSub key={it.sub} id={it.sub} label={it.label} icon={it.icon} forceOpen={!!mobileMenuQuery || it.items.some((c) => c.to === navFocusPath)} active={it.items.some((c) => location.pathname === c.to || (!c.exact && location.pathname.startsWith(`${c.to}/`)))}>
                    {it.items.map((c) => <NavItem key={c.to} to={c.to} exact={c.exact} icon={c.icon} label={c.label} dot={c.dot} dotTestid={c.dotTestid} dotTo={c.dotTo} dotTitle={c.dotTitle} />)}
                  </NavSub>
                ) : (
                  <React.Fragment key={it.to}>
                    {it.divider && !(rail && !mobileNav) && <div className="px-3 pt-2.5 pb-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">{it.divider}</div>}
                    <NavItem to={it.to} exact={it.exact} icon={it.icon} label={it.label} dot={it.dot} dotTestid={it.dotTestid} dotTo={it.dotTo} dotTitle={it.dotTitle} />
                  </React.Fragment>
                ))}
              </NavGroup>
            );
          })}
          </RailCtx.Provider>
        </nav>
        <div className="p-3 border-t border-slate-100 shrink-0">
          {/* 手机设置收进完整菜单，腾出顶栏空间给搜索、打卡和通知。 */}
          <div className="flex items-center justify-between gap-2 2xl:hidden px-2 pb-2">
            <button type="button" data-testid="mobile-tools-open" aria-label={zh ? "设置与工具" : "Settings & tools"} title={zh ? "设置与工具" : "Settings & tools"} className={`min-h-11 shrink-0 flex items-center justify-center gap-2 rounded-xl bg-slate-50 text-sm font-medium text-slate-700 ${rail && !mobileNav ? "w-11 -mx-2" : "px-3"}`} onClick={() => { setMobileNav(false); setMobileToolsOpen(true); }}><SunMoon className="h-5 w-5 shrink-0" />{!(rail && !mobileNav) && (zh ? "设置与工具" : "Settings & tools")}</button>
          </div>
          <div className={`flex items-center gap-3 ${rail && !mobileNav ? "flex-col px-0 py-1" : "px-2 py-2"}`}>
            <button data-testid="sidebar-avatar-btn" onClick={() => fileRef.current?.click()} className="relative group rounded-full flex-shrink-0" title={lang === "zh" ? "点击上传头像" : "Upload avatar"}>
              <Avatar src={user.avatar} name={lang === "zh" ? user.nameZh : user.name} size={36} />
              <span className="absolute inset-0 rounded-full bg-slate-900/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                <Camera className="h-4 w-4 text-white" />
              </span>
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={onPickAvatar} className="hidden" data-testid="sidebar-avatar-input" />
            <AvatarCropDialog file={cropFile} zh={lang === "zh"} busy={uploading} onDone={onCropped} onCancel={() => { if (!uploading) setCropFile(null); }} />
            {!(rail && !mobileNav) && (
              <div className="flex-1 min-w-0">
                {/* 个人信息只展示姓名，不公开职位、角色级别及其区域副标题；仅调整展示，菜单与业务权限仍沿用账号设置。 */}
                {/* 中文名可能未填写，尤其是管理员代操作时；沿用既有姓名口径回退到英文名，避免侧栏空白。 */}
                <p className="text-slate-900 text-sm font-semibold truncate" data-testid="sidebar-account-name">{lang === "zh" ? (user.nameZh || user.name) : user.name}</p>
                {user.positionRole === "sales_assistant" && <p className={`text-[10px] truncate ${user.assisting ? "text-indigo-600" : "text-amber-600"}`} data-testid="sidebar-assisting">{user.assisting ? (lang === "zh" ? `协助 ${user.assisting.nameZh || user.assisting.name}` : `Assisting ${user.assisting.name}`) : (lang === "zh" ? "未绑定销售人员，请联系管理员" : "Not paired with a salesperson")}</p>}
              </div>
            )}
            <button onClick={handleLogout} data-testid="sidebar-logout-btn" title={lang === "zh" ? "退出登录" : "Sign out"} className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>
      </ResponsiveSidebar>

      {/* Main */}
      <div className={`flex-1 flex flex-col min-h-screen min-w-0 transition-[padding] duration-200 ${rail ? "lg:pl-[76px]" : "lg:pl-64"}`}>
        <header className="app-topbar h-16 bg-white/85 backdrop-blur-md border-b border-slate-200/60 flex items-center gap-2 sm:gap-3 px-3 sm:px-6 sticky top-0 z-30" data-testid="app-topbar">
          <button aria-label={zh ? "打开菜单" : "Open menu"} aria-expanded={mobileNav} aria-haspopup="dialog" className="lg:hidden h-11 w-11 shrink-0 rounded-xl flex items-center justify-center text-slate-600 hover:bg-slate-100 transition-colors" data-testid="mobile-nav-open" onClick={() => setMobileNav(true)}>
            <Menu className="h-5 w-5" />
          </button>
          <button className="hidden lg:flex h-9 w-9 rounded-xl items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors" data-testid="sidebar-collapse-toggle" onClick={toggleRail} title={rail ? (lang === "zh" ? "展开侧栏" : "Expand sidebar") : (lang === "zh" ? "收起侧栏" : "Collapse sidebar")}>
            {rail ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
          </button>
          <div className="hidden md:flex lg:hidden items-center shrink-0"><BrandLogo size={28} compact testid="topbar-brand" /></div>
          {/* 全局搜索入口（2026-10-20 用户口径；2026-10-21 改为「纯图标入口」）：
              ① SYS-002 时期的「只找页面」限制已放开，恢复跨模块搜索（客户 / 项目 / 频道 / 同事 / 页面），
                 结果仍按账号权限由后端 /search/global 过滤，不会搜出越权内容；
              ② 【2026-10-21 用户口径，最终方案】顶栏只留一个放大镜按钮，点击直接弹出搜索面板。
                 为什么不再在顶栏摆「全局搜索」标签 + 整句提示词：
                   · 顶栏是一条拥挤的横向空间，放长文案就得跟两地时钟 / 撤销 / 语言 / 通知抢位置，
                     1280~1799 这一段尤其明显，之前只能靠层层断点降级（隐藏键位提示、收标签、
                     收紧内边距）来凑空间，既脆弱又难维护；
                   · 而提示词放哪都不会影响顶栏 —— 弹窗是浮层，宽度自己做主，
                     把「能搜什么」完整写在弹窗输入框里，比在顶栏挤一句半截话清楚得多。
                 结论：顶栏回归最小占位（36×36 图标按钮），搜索能力的说明交给弹窗。
              ③ 快捷键仍然可用：⌘K / Ctrl+K 全局唤起（见下方 keydown 监听），
                 按钮 title 与 aria-label 都写着「全局搜索 (⌘K)」，鼠标悬停即可发现。 */}
          <button aria-label={zh ? "全局搜索" : "Global search"} onClick={() => setSearchOpen(true)} data-testid="global-search-btn" className="shrink-0 flex items-center justify-center h-11 w-11 md:h-9 md:w-9 rounded-xl border border-slate-200 bg-white/70 text-slate-500 hover:border-brand-300 hover:text-brand-600 transition-colors" title={lang === "zh" ? "全局搜索 (⌘K)" : "Global search (⌘K)"}>
            <Search className="h-[18px] w-[18px]" />
          </button>
          <div className="flex-1" />
          {/* 双城时间统一常驻吸顶栏，页面不再叠加右下角浮窗，避免遮挡业务内容。 */}
          {!isSub && <WorldClock />}
          {!isSub && <div className="hidden md:flex"><TodoPeek teamOpen={teamOpenTodos} agendaCrm={agendaCrm} onPendingChange={setMobileTodoCount} /></div>}
          <ClockWidget />
          {/* 撤销：始终可点，点开「操作记录」面板查看历史并选择撤销某一步（无记录时面板内展示缺省提示） */}
          <button onClick={() => setUndoOpen(true)} data-testid="topbar-undo"
            className="hidden whitespace-nowrap sm:flex items-center gap-1.5 text-sm rounded-full px-2.5 py-1.5 transition-colors text-slate-600 hover:text-brand-600 hover:bg-slate-100">
            <Undo2 className="h-4 w-4 shrink-0" /> {lang === "zh" ? "\u64A4\u9500" : "Undo"}
          </button>
          {/* 分包商手机首屏可直接选语言；桌面语言组及其他角色的设置入口沿用原有规则。 */}
          {isSub && <MobileLanguageMenu lang={lang} onSelect={setLang} />}
          <div data-no-i18n data-testid="topbar-lang-toggle" className="hidden sm:inline-flex items-center rounded-full border border-slate-200 bg-white p-0.5 text-xs font-semibold shadow-sm" title={lang === "zh" ? "切换语言 · Language · Idioma" : lang === "en" ? "Language · 切换语言 · Idioma" : "Idioma · Language · 切换语言"}>
            {[["zh", "中"], ["en", "EN"], ["es", "ES"]].map(([code, label]) => (
              <button key={code} type="button" onClick={() => setLang(code)} data-testid={`topbar-lang-${code}`} aria-pressed={lang === code}
                className={`px-2.5 py-1 rounded-full transition-colors ${lang === code ? "bg-brand-grad text-white shadow-brand" : "text-slate-500 hover:text-slate-800"}`}>{label}</button>
            ))}
          </div>
          <div className="hidden md:block">
            <DisplayMenu zh={lang === "zh"} theme={theme} onSave={savePrefs} usingDefaults={!!prefs && Object.keys(prefs.defaults || {}).length > 0 && !(prefs.customized || []).length} landing={prefs?.landing || ""} landingOptions={landingOptions} onLanding={setLanding} />
          </div>
          <button onClick={() => setTourOpen(true)} data-testid="tour-help-btn" className="hidden lg:flex h-9 w-9 rounded-xl items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors" title={lang === "zh" ? "查看新手引导" : "Show tour"}>
            <HelpCircle className="h-5 w-5" />
          </button>
          <NavLink to="/reminders" className="relative text-slate-300 hover:text-brand-600 hidden" data-testid="topbar-bell-link">
            <Bell className="h-5 w-5" />
          </NavLink>
          <div className="relative mr-1 sm:mr-0">
            <button aria-label={zh ? "通知提醒" : "Notifications"} aria-expanded={bellOpen} onClick={toggleBell} className={`relative h-11 w-11 md:h-9 md:w-9 shrink-0 rounded-xl flex items-center justify-center transition-colors ${unread > 0 ? "text-rose-600 bg-rose-50 hover:bg-rose-100" : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"}`} data-testid="topbar-bell">
              <Bell className={`h-5 w-5 ${unread > 0 ? "animate-pulse" : ""}`} />
              {unread > 0 && <span data-testid="topbar-unread-badge" className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-rose-600 text-white text-[10px] flex items-center justify-center font-bold ring-2 ring-white">{unread > 99 ? "99+" : unread}</span>}
            </button>
            {bellOpen && (
              <>
                {createPortal(<div className="fixed inset-0 z-10" onClick={() => setBellOpen(false)} />, document.body)}
                <div className="absolute right-0 top-11 z-50 w-80 max-w-[calc(100vw-1.5rem)] rounded-2xl border border-slate-200/80 bg-white shadow-lift overflow-hidden" data-testid="topbar-bell-dropdown">
                  <p className="px-4 py-2.5 text-xs font-bold text-slate-600 border-b border-slate-100 flex items-center justify-between gap-2">
                    <span>{lang === "zh" ? "未读提醒" : "Unread"} <span className="text-rose-600">{unread}</span></span>
                    {unread > 0 && <button type="button" data-testid="topbar-bell-read-all" onClick={async () => { const n = await markAllRemindersRead(); toast.success(lang === "zh" ? `已将 ${n} 条提醒标为已读` : `${n} marked read`); }} className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand-600 hover:text-brand-800"><CheckCheck className="h-3.5 w-3.5" />{lang === "zh" ? "全部已读" : "Mark all read"}</button>}
                  </p>
                  {/* 分类页签：评论 / @我、催办、系统自动提醒分开（反馈 FB973a773a3a） */}
                  <div className="flex items-center gap-1 px-2 py-1.5 border-b border-slate-100 bg-slate-50/60" data-testid="topbar-bell-cats">
                    {[["all", lang === "zh" ? "全部" : "All", bellCatCounts.all], ["comment", lang === "zh" ? "评论" : "Comments", bellCatCounts.comment], ["nudge", lang === "zh" ? "催办" : "Nudges", bellCatCounts.nudge], ["system", lang === "zh" ? "系统" : "System", bellCatCounts.system]].map(([k, label, n]) => (
                      <button key={k} type="button" data-testid={`topbar-bell-cat-${k}`} data-active={bellCat === k ? 1 : 0} onClick={() => setBellCat(k)}
                        className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-lg transition-colors ${bellCat === k ? (BELL_CAT_ACTIVE[k] || "bg-slate-900 text-white") : "text-slate-500 hover:bg-slate-100"}`}>
                        {label}<span className={`text-[10px] ${bellCat === k ? "opacity-80" : "text-slate-400"}`}>{n}</span>
                      </button>
                    ))}
                  </div>
                  <div className="max-h-[50vh] overflow-y-auto">
                    {bellList.length === 0 ? (
                      <p className="px-4 py-6 text-center text-xs text-slate-300" data-testid="topbar-bell-empty">{bellCat === "comment" ? (lang === "zh" ? "暂无评论 / @提及" : "No comments") : bellCat === "nudge" ? (lang === "zh" ? "暂无催办" : "No nudges") : (lang === "zh" ? "暂无未读提醒" : "All caught up")}</p>
                    ) : bellList.slice(0, 8).map((r) => (
                      <div key={r.id} className="relative border-b border-slate-50 hover:bg-brand-50/40">
                        <button data-testid={`topbar-bell-item-${r.id}`}
                          onClick={() => openBellDetail(r)}
                          className={`w-full text-left px-4 py-2.5 ${r.meta?.copyText || r.meta?.attachment ? "pr-20" : ""}`}>
                          <p className="text-[12px] text-slate-700 leading-snug line-clamp-2">
                            {!r.read && <span data-testid={`topbar-bell-dot-${r.id}`} title={lang === "zh" ? "未读" : "Unread"} className="inline-block h-1.5 w-1.5 rounded-full bg-rose-500 mr-1.5 align-middle" />}
                            <span className={r.read ? "text-slate-400" : ""}>{fmtText(T(bellMsg(r)))}</span>
                          </p>
                          <p className="text-[10px] text-slate-400 mt-0.5">
                            {r.read ? (lang === "zh" ? "已读 · " : "Read · ") : ""}{fmtDT(r.createdAt)}
                          </p>
                        </button>
                        {renderBellInline(r)}
                        {r.meta?.attachment?.api && (
                          <button type="button" data-testid={`topbar-bell-attachment-${r.id}`} title={r.meta.attachment.label || "PDF"}
                            onClick={(e) => { e.stopPropagation(); openAuthedPdf(api, r.meta.attachment.api, `${r.meta.attachment.label || "attachment"}.pdf`).catch(() => toast.error(lang === "zh" ? "附件打开失败" : "Failed to open attachment")); }}
                            className="absolute right-3 top-2.5 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full bg-slate-800 text-white hover:bg-slate-700">
                            <FileStack className="h-3 w-3" />{r.meta.attachment.label || (lang === "zh" ? "附件 PDF" : "PDF")}
                          </button>
                        )}
                        {r.meta?.copyText && (
                          <button type="button" data-testid={`topbar-bell-copy-${r.id}`} title={lang === "zh" ? "复制付款链接" : "Copy payment link"}
                            onClick={async (e) => { e.stopPropagation(); try { await navigator.clipboard.writeText(r.meta.copyText); toast.success(lang === "zh" ? "付款链接已复制，可直接转发业主" : "Payment link copied"); } catch { toast.error(r.meta.copyText); } }}
                            className="absolute right-3 top-2.5 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full bg-emerald-600 text-white hover:bg-emerald-700">
                            <Link2 className="h-3 w-3" />{lang === "zh" ? "复制链接" : "Copy"}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  {/* 【2026-10-28 修复】落点由 reminderCenterPath() 收口：无权进提醒中心的账号（如分包商）
                      不再被送到「无权访问该模块」的空白页，而是回其默认首页。 */}
                  <button data-testid="topbar-bell-all" onClick={() => { setBellOpen(false); const base = reminderCenterPath(); nav(bellCat === "all" || base !== "/reminders" ? base : `/reminders?cat=${bellCat}`); }}
                    className="w-full text-center text-xs font-semibold text-brand-600 hover:bg-brand-50 py-2.5">{lang === "zh" ? "查看全部提醒 →" : "View all →"}</button>
                </div>
              </>
            )}
            <Dialog open={!!bellDetail} onOpenChange={(o) => { if (!o) setBellDetail(null); }}>
              <DialogContent className="max-w-lg" data-testid="topbar-bell-detail-modal">
                <DialogHeader>
                  <DialogTitle className="text-sm font-semibold text-slate-800">{lang === "zh" ? "提醒详情" : "Reminder detail"}</DialogTitle>
                </DialogHeader>
                {bellDetail && (
                  <div className="relative space-y-3">
                    <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap break-words" data-testid="topbar-bell-detail-message">{fmtText(T(bellMsg(bellDetail)))}</p>
                    <div className="text-[11px] text-slate-400">{bellDetail.type} · {fmtDT(bellDetail.createdAt)}</div>
                    {renderBellInline(bellDetail)}
                    <div className="flex items-center justify-end gap-2 pt-1">
                      {(bellDetail.link || bellDetail.type === "mention") && (
                        <button type="button" data-testid="topbar-bell-detail-go" onClick={() => bellGo(bellDetail)} className="px-3 py-1.5 rounded-lg bg-brand-500 text-white text-sm font-medium hover:bg-brand-600">{lang === "zh" ? "去处理" : "Open"}</button>
                      )}
                      <button type="button" data-testid="topbar-bell-detail-close" onClick={() => setBellDetail(null)} className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">{lang === "zh" ? "关闭" : "Close"}</button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
          {/*
            2026-10-14 新增（需求：系统铃铛后方增加「需求反馈」入口）：
            入口按钮 + 未读回复角标 + 提交/我的列表/详情抽屉，全部封装在 FeedbackButton 内。
            pages 传入「当前账号可见的菜单」扁平表，供提交表单里的「问题页面」搜索选择
            （复用 vis() 过滤，避免让用户选到自己根本进不去的页面）。
          */}
          {!isSub && <FeedbackButton pages={landingOptions} />}
        </header>
        <BreadcrumbSlotContext.Provider value={breadcrumbSlotRef}>
        {/*
          2026-10-13 追加（需求：用户「为什么底部会有一部分没有在内容区域内」）：
          main 改成 flex 纵向容器，配合 `.app-page-content` 的 `flex:1 0 auto`（见 index.css），
          让内容区吃掉 main 的剩余高度 —— 否则「内容比视口矮」的页面会在 main 底部空出一条
          不属于内容区的空白带（真机实测 /admin/users 差 24.9px，DevTools 高亮框盖不住）。
          ⚠️ 这里**故意不加 `min-h-0`**：main 自己是外层（`min-h-screen` flex-col）的弹性子项，
          保持默认 `min-height:auto` 才不会被压到内容高度以下；一旦允许压缩，长页面内容会溢出
          main 盒子、整页滚动失效。main 的高度仍是 `flex-1` 撑到底，视觉零变化。
        */}
        <main className="flex-1 min-w-0 flex flex-col">
          {/*
            面包屑条：与顶部栏（app-topbar）保持同一套「滚动毛玻璃」观感。
            需求（本次改动）：面包屑块滚动后要像顶部栏一样是半透明毛玻璃 ——
              原实现用不透明 `bg-white`，页面滚动时下方的正文/工具条滑到它背后会被
              整块实心白遮死，与顶部栏（bg-white/85 + backdrop-blur-md，内容能透出模糊轮廓）
              的观感割裂，越往上滚越像一条「实心白板压在内容上」。
            最终效果：滚动时面包屑背后的内容透出并虚化，未滚动时（背后无内容）视觉上
              仍是接近纯白的条带，宽度/高度/吸顶位置与改前完全一致（不引入布局位移）。
            关键取舍：透明度、模糊强度、描边色全部照抄顶部栏常量（/85 + backdrop-blur-md +
              border-slate-200/60），不另起一套数值，避免以后两处观感再次漂移；
              不使用「滚动监听 + 条件切换 class」的方案 —— 顶部栏本身就是常驻毛玻璃，
              跟随它才能保证两条吸顶条行为一致，也免掉滚动事件的额外开销。
          */}
          {/*
            面包屑条左右内边距必须与页面内容对齐（否则面包屑文字会比正文多缩进一档），
            故与 .app-page-content 共用同一个变量类 .page-pad-x，而不是写死 px-4 sm:px-6 lg:px-7。
            2026-10-13：页面留白由 16/24/28px 收为 16/20/24px，这里随变量自动跟随，无需再改。
          */}
          <div
            className="sticky below-topbar z-20 border-b border-slate-200/60 bg-white/85 backdrop-blur-md page-pad-x py-2.5"
            data-testid="page-breadcrumb-bar"
          >
            <div className="flex items-center justify-between gap-3">
              {/* 左：面包屑（min-w-0 让过长时能横向滚动）；右：动作插槽（页面 portal 注入，如「项目财务」的收款报表按钮） */}
              <div className="min-w-0 flex-1">
                <PageBreadcrumb navDef={NAVDEF} pathname={location.pathname} lang={lang} />
              </div>
              <div ref={breadcrumbSlotRef} className="flex shrink-0 items-center gap-2" data-testid="page-breadcrumb-actions" />
            </div>
          </div>
          {/*
            页面内容容器：**全站唯一的「页面级内边距」来源**。
            需求：页面内容与面包屑白条 / 侧栏之间只保留一层留白，不允许出现两层边距。
            为什么放在这里：此前部分页面的根元素又自行写了一遍 `p-4 md:p-6`，与本行的
            页面留白叠加成两层 —— 真机实测大屏下正文左边距达 52px（当时 28+24），
            视觉上比其它页面明显更空、且各页宽窄不一。

            2026-10-13 调整（需求：用户「这个 padding 收小一点」）：
              三档由 16 / 24 / 28px 收为 16 / 20 / 24px，并把**左右**留白抽成 CSS 变量
              `--page-pad-x`（定义见 index.css，上下仍由下面的 py-* 控制）。
              抽变量的目的：① 改档位只改一处；②「紧凑密度」只需覆盖该变量，
              所有通栏条自动跟随，不会出现「某档负边距对不上、通栏条左右错位」。

            约定（新增页面请遵守）：
              1. 页面根元素**不要**再声明页面级 padding（p-4/p-6/p-8 等），只保留
                 `space-y-*`（块间距）、`max-w-*` + `mx-auto`（宽度约束与居中）。
              2. 页面内若需要「通栏」的条状元素（sticky 工具条等），用 `.page-bleed-x`
                 抵消左右留白、再配 `.page-pad-x` 回补内容内边距（必要时配 `-mt-*` 抵消上留白），
                 参考 pages/SalesWorkspace.jsx 顶部 tabs、pages/CRMSignedCustomerHome.jsx 顶部工具条。
                 ⚠️ 不要再写死 `-mx-4 sm:-mx-6 lg:-mx-7`：左右留白已是变量（普通档 16/20/24px、
                 紧凑档 12/14/16px），写死的负边距在紧凑模式下会与真实留白对不上 → 通栏条左右错位。
              3. 上下断点对应关系：本行 py 三档 = sm 以下 16px / sm 20px / lg 24px。
          */}
          <div className="app-page-content py-4 sm:py-5 lg:py-6">
            {user.impersonatedBy && <ImpersonationBanner />}
            <RecycleSurgeBanner />
            {showTopAgenda && <WeekAgendaCalendar />}
            {/* 2026-10-14 走查清单「二、重复冗余 #12」：原 <KickoffBanner /> 待开工横幅与
                「工程项目」页内 KickoffCountdownCards 重复展示同一批项目，已合并到倒计时卡，
                横幅组件及其 import 一并移除（分配小组 / 分配规则能力已并入 KickoffCountdownCards）。*/}
            {/* 代操作切换账号后，不应沿用上个账号的页面；当前页对当前账号不可见 → 回到该账号的默认首页。
                【2026-10-28 修复「代操作下刷新被莫名弹走」】原实现只看 navVis(location.pathname)，
                但 navVis 依赖权限快照：快照还没加载完时 permOf 返回 undefined，vis() 会退回该页面的
                写死默认 def（/branch-staff 是 false），于是**刷新/重新打开**时明明有权限的页面也被
                判为不可见，被静默弹到落地页；而点侧栏进入时快照已就绪，页面又能正常留下 —— 同一页面
                两种结果，用户只会觉得「刷一下就跑到别处去了」。
                现在要求「快照已加载（permMap 非空）」才做这个判断；快照未就绪时先渲染当前页，
                等快照回来后再按真实权限决定（那时若真无权限，下面的 pathDenied / 页面自身守卫照常生效）。*/}
            {user.impersonatedBy && permMap && !navVis(location.pathname) ? (
              <Navigate to={defaultLanding(user) || "/"} replace />
            ) : (pathDenied && pathDenied(location.pathname)) || adminPathBlocked ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-24" data-testid="module-access-denied">
                <ShieldCheck className="h-12 w-12 text-slate-300 mb-3" />
                <p className="text-lg font-semibold text-slate-600">{lang === "zh" ? "无权访问该模块" : "Access denied"}</p>
                <p className="text-sm text-slate-400 mt-1">{lang === "zh" ? "该模块已被管理员限制访问，请联系管理员。" : "This module is restricted by the administrator."}</p>
              </div>
            ) : <Outlet />}
          </div>
        </main>
        <MobileNavigation items={mobileWork} showTodos={!isSub && !(pathDenied && pathDenied("/todos"))} todoCount={mobileTodoCount} zh={zh} menuOpen={mobileNav} onMenu={() => setMobileNav(true)} />
        </BreadcrumbSlotContext.Provider>
      </div>
      <MobileMoreMenu zh={zh} lang={lang} open={mobileToolsOpen} onOpenChange={setMobileToolsOpen} hideTrigger onToggleLang={toggleLang} onTour={() => setTourOpen(true)} onUndo={undo} canUndo={canUndo} onOpenHistory={() => setUndoOpen(true)}
        display={{ onSave: savePrefs, usingDefaults: !!prefs && Object.keys(prefs.defaults || {}).length > 0 && !(prefs.customized || []).length, landing: prefs?.landing || "", landingOptions, onLanding: setLanding }} />
      {eod && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/45 backdrop-blur-[2px] p-4" data-testid="eod-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="bg-red-600 text-white px-5 py-4">
              <h3 className="text-lg font-bold flex items-center gap-2"><LogOut className="h-5 w-5" />{lang === "zh" ? "下班前请先完成今日待办" : "Finish today's tasks before you leave"}</h3>
              <p className="text-sm text-red-100 mt-0.5">{lang === "zh" ? `你还有 ${eod.pending} 项今日待办（含逾期）未完成，无法打卡下班。` : `You have ${eod.pending} unfinished item(s); you can't clock out.`}</p>
            </div>
            <div className="p-4 overflow-y-auto space-y-2">
              {(eod.items || []).filter((it) => it.day === "overdue" || it.day === eod.today).map((it) => (
                <div key={it.id} className="flex items-center gap-2 rounded-lg border border-red-100 bg-red-50/50 p-2.5" data-testid={`eod-item-${it.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800 text-sm truncate">{it.title}</p>
                    <p className="text-[11px] text-red-600 font-bold">{it.kind === "crm" ? (lang === "zh" ? "客户今日必做" : "CRM must-do") : (lang === "zh" ? "待办" : "To-do")}{it.overdue ? (lang === "zh" ? " · 逾期" : " · overdue") : ""}</p>
                  </div>
                  <button data-testid={`eod-open-${it.id}`} onClick={() => { setEod(null); nav(it.link || "/todos"); }} className="text-xs text-indigo-600 border border-indigo-200 rounded px-2 py-1 hover:bg-indigo-50">{lang === "zh" ? "去处理" : "Open"}</button>
                </div>
              ))}
              {eod.err && <p className="text-xs text-red-600 text-center">{eod.err}</p>}
            </div>
            <div className="border-t border-slate-100 p-3 flex items-center justify-between gap-2">
              <button data-testid="eod-goto-list" onClick={() => { setEod(null); nav("/"); }} className="text-sm font-medium text-indigo-600 hover:underline">{lang === "zh" ? "打开本周待办" : "Open my week"}</button>
              <div className="flex items-center gap-2">
                <button data-testid="eod-stay" onClick={() => setEod(null)} className="text-sm text-slate-500 px-3 py-1.5 rounded-lg hover:bg-slate-100">{lang === "zh" ? "留下处理" : "Stay"}</button>
                <button data-testid="eod-clock-out" onClick={eodClockOut} className="text-sm text-white bg-slate-300 cursor-not-allowed px-3 py-1.5 rounded-lg" disabled>{lang === "zh" ? `还有 ${eod.pending} 项` : `${eod.pending} left`}</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {user.mustChangePassword && <ForcePasswordChange />}
      {celebration && (
        <div className="fixed inset-0 z-[100] bg-slate-900/45 backdrop-blur-[2px] flex items-center justify-center p-4" data-testid="sign-celebration-overlay">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-brand-grad" />
            <PartyPopper className="h-14 w-14 mx-auto text-amber-500 animate-bounce" />
            <h3 className="text-xl font-black text-slate-800 mt-3">{lang === "zh" ? "恭喜签约成功！" : "Contract signed!"}</h3>
            <p className="text-sm text-slate-500 mt-2 leading-relaxed" data-testid="celebration-message">{celebration.message}</p>
            <div className="flex gap-2 justify-center mt-6">
              <button data-testid="celebration-view-lead" onClick={() => { markReminderRead(celebration.id); const l = celebration.link; setCelebration(null); if (l) nav(l); }} className="btn-brand !rounded-full !px-5 !py-2 text-sm font-bold">{lang === "zh" ? "查看客户 · 安排设计与现场" : "View lead"}</button>
              <button data-testid="celebration-close" onClick={() => { markReminderRead(celebration.id); setCelebration(null); }} className="px-5 py-2 rounded-full border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50">{lang === "zh" ? "知道了" : "Got it"}</button>
            </div>
          </div>
        </div>
      )}
      <Softphone chatUnread={chatUnread} />
      <AssistantWidget />
      <InstallPrompt zh={lang === "zh"} />
      {tourOpen && <WelcomeTour steps={TOUR_STEPS} zh={lang === "zh"} onClose={closeTour} />}
      {/* 撤销后面板保持打开：让用户在原地看到列表实时缩短（历史已随之被裁剪） */}
      <UndoHistoryPanel open={undoOpen} onClose={() => setUndoOpen(false)} zh={lang === "zh"} lang={lang} history={history}
        onUndoAt={(idx) => { undoTo(idx); }} />
      {/* 全局搜索（2026-10-20 恢复跨模块）：客户 / 工程项目 / 设计项目 / 频道 / 同事交给后端
          /search/global 按账号权限返回，页面结果仍由这里传入的可见菜单决定。
          权限：pages 只传当前账号可见的菜单；canSee 再兜一层（角色默认 + 账号级授权/屏蔽 + 路径屏蔽），
          保证无权限的页面既不出现在结果里，也不能靠输入路由猜出来（用户 2026-10-19 要求）。 */}
      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} pages={flatNav.filter((it) => it.to && vis(it.to, it.def, it.when))} canSee={(p) => vis(p.to, p.def, p.when) && !(pathDenied && pathDenied(p.to))} />
    </div>
    </FavCtx.Provider>
    </RailCtx.Provider>
  );
}
